import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart2, RefreshCw, TrendingUp, TrendingDown, ArrowUp, ArrowDown,
  Search, ShieldCheck, Zap, CheckCircle, AlertTriangle, XCircle,
  ChevronDown, ChevronRight, ExternalLink, ShoppingCart, Activity, Play, History,
  Cpu, Pause, Power, DollarSign, BarChart,
} from 'lucide-react'
import { rebalanceAPI, paperAPI } from '../services/api'
import OrderModal from '../components/OrderModal'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
const fmtInr = n => n == null ? '—' : `₹${fmt(Math.abs(n), 0)}`
const pct = n => n == null ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi)

// Score → colour
const scoreBg  = s => s >= 65 ? 'bg-success-100 text-success-700' : s >= 45 ? 'bg-amber-100 text-amber-700' : 'bg-danger-100 text-danger-700'
const scoreBar = s => s >= 65 ? 'bg-success-500' : s >= 45 ? 'bg-amber-400' : 'bg-danger-500'

// Decision badge
const ACTION_META = {
  KEEP:     { color: 'bg-success-100 text-success-700 border-success-200', icon: CheckCircle,  label: 'Keep'     },
  INCREASE: { color: 'bg-blue-100 text-blue-700 border-blue-200',          icon: ArrowUp,      label: 'Increase' },
  REDUCE:   { color: 'bg-amber-100 text-amber-700 border-amber-200',        icon: ArrowDown,    label: 'Reduce'   },
  EXIT:     { color: 'bg-danger-100 text-danger-700 border-danger-200',     icon: XCircle,      label: 'Exit'     },
}

function ActionBadge({ action }) {
  const m = ACTION_META[action] || ACTION_META.KEEP
  const Icon = m.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${m.color}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  )
}

function RegimeBadge({ regime }) {
  const colors = { BULL: 'bg-success-100 text-success-700', BEAR: 'bg-danger-100 text-danger-700', SIDEWAYS: 'bg-amber-100 text-amber-700', UNKNOWN: 'bg-gray-100 text-gray-500' }
  const labels = { BULL: '🟢 Bull Market', BEAR: '🔴 Bear Market', SIDEWAYS: '🟡 Sideways', UNKNOWN: '⚪ Unknown' }
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors[regime] || colors.UNKNOWN}`}>
      {labels[regime] || regime}
    </span>
  )
}

// Horizontal score bar inline
function ScoreBar({ score }) {
  const s = Math.round(score ?? 0)
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${scoreBar(s)}`} style={{ width: `${s}%` }} />
      </div>
      <span className={`text-xs font-bold min-w-[26px] text-right ${
        s >= 65 ? 'text-success-700' : s >= 45 ? 'text-amber-700' : 'text-danger-700'
      }`}>{s}</span>
    </div>
  )
}

// Expandable reasons row
function ReasonsList({ reasons }) {
  if (!reasons?.length) return null
  return (
    <ul className="mt-1.5 space-y-0.5">
      {reasons.map((r, i) => (
        <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
          <span className="mt-0.5 text-gray-300 shrink-0">›</span> {r}
        </li>
      ))}
    </ul>
  )
}

// Capital chip
function CapChip({ label, value, positive }) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className={`text-lg font-bold ${positive ? 'text-success-600' : 'text-gray-800'}`}>{fmtInr(value)}</p>
    </div>
  )
}

// ── Sort headers — defined OUTSIDE the component so React always sees the
// same component type and never unmounts/remounts the <th> DOM node.
// This ensures click events are always delivered, even during rapid state changes.

const SortTh = ({ field, label, right = false, sortKey, sortDir, onSort }) => {
  const active = sortKey === field
  return (
    <th
      className={`px-3 py-2.5 font-medium text-gray-500 cursor-pointer select-none hover:bg-gray-100 transition-colors whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary-500" /> : <ArrowDown className="w-3 h-3 text-primary-500" />)
          : <ArrowUp className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  )
}

const CandTh = ({ field, label, right = true, sortKey, sortDir, onSort }) => (
  <th
    onClick={() => onSort(field)}
    className={`px-3 py-2.5 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors ${right ? 'text-right' : 'text-left'}`}
  >
    {label}
    {sortKey === field && <span className="ml-1 opacity-60">{sortDir === 'asc' ? '↑' : '↓'}</span>}
  </th>
)

const HistTh = ({ field, label, right = true, sortKey, sortDir, onSort }) => (
  <th
    onClick={() => onSort(field)}
    className={`px-3 py-2.5 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors ${right ? 'text-right' : 'text-left'}`}
  >
    {label}
    {sortKey === field && <span className="ml-1 opacity-60">{sortDir === 'asc' ? '↑' : '↓'}</span>}
  </th>
)

// ── Main component ─────────────────────────────────────────────────────────────
const Rebalance = () => {
  const [tab, setTab]             = useState('scorecard')
  const [status, setStatus]       = useState(null)
  const [holdings, setHoldings]   = useState([])
  const [candidates, setCandidates] = useState([])
  const [plan, setPlan]           = useState(null)

  const [loading, setLoading]       = useState(true)
  const [scoring, setScoring]       = useState(false)
  const [generating, setGenerating] = useState(false)
  const [scoringUniverse, setScoringUniverse] = useState(false)

  const [search, setSearch]       = useState('')
  const [sectorFilter, setSectorFilter] = useState('')
  const [sortKey, setSortKey]     = useState('compositeScore')   // worst first = asc
  const [sortDir, setSortDir]     = useState('asc')
  const [expanded, setExpanded]   = useState({})    // symbol → bool (show reasons)

  const [orderModal, setOrderModal] = useState({ isOpen: false, stock: null, action: 'SELL', qty: 0, price: 0 })

  // History + Backtest state
  const [historyMap, setHistoryMap] = useState({})    // symbol → array of history points
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [backtests, setBacktests] = useState([])
  const [activeBacktest, setActiveBacktest] = useState(null)
  const [runningBacktest, setRunningBacktest] = useState(false)
  const [btConfig, setBtConfig] = useState({
    startDate: '2025-10-01',
    endDate:   new Date().toISOString().slice(0, 10),
    topN: 5,
    initialCapital: 1000000,
  })

  // History tab sort/search
  const [histSearch, setHistSearch]       = useState('')
  const [histSortKey, setHistSortKey]     = useState('latest')   // latest | delta | symbol | points
  const [histSortDir, setHistSortDir]     = useState('asc')      // asc = worst first

  // Candidates tab sort/search
  const [candSearch, setCandSearch]       = useState('')
  const [candSectorFilter, setCandSectorFilter] = useState('')
  const [candSortKey, setCandSortKey]     = useState('compositeScore')
  const [candSortDir, setCandSortDir]     = useState('desc')

  // Paper trading state
  const [paperStatus, setPaperStatus]       = useState(null)
  const [paperConfig, setPaperConfig]       = useState(null)
  const [paperPositions, setPaperPositions] = useState([])
  const [paperTrades, setPaperTrades]       = useState([])
  const [paperPerf, setPaperPerf]           = useState(null)
  const [paperLoaded, setPaperLoaded]       = useState(false)
  const [paperCycleRunning, setPaperCycleRunning] = useState(false)

  // Phase 3 Research Platform
  const [researchData, setResearchData]         = useState(null)
  const [researchRejected, setResearchRejected] = useState(null)
  const [researchLoaded, setResearchLoaded]     = useState(false)
  const [researchSub, setResearchSub]           = useState('overview')

  // Prediction tracking (score-vs-return validation)
  const [svr, setSvr]                   = useState(null)   // score-vs-return analysis
  const [svrLoaded, setSvrLoaded]       = useState(false)
  const [resolvingFwd, setResolvingFwd] = useState(false)
  const [svrWindow, setSvrWindow]       = useState(30)     // active window: 7 | 30 | 90
  const [svrView, setSvrView]           = useState('buckets') // 'buckets' | 'deciles'

  // Load everything in parallel
  const load = useCallback(async () => {
    setLoading(true)
    const [statusR, holdingsR, candidatesR, plansR] = await Promise.allSettled([
      rebalanceAPI.getStatus(),
      rebalanceAPI.getHoldingsScored(),
      rebalanceAPI.getCandidates(20),
      rebalanceAPI.getPlans(),
    ])
    if (statusR.status    === 'fulfilled') setStatus(statusR.value.data)
    if (holdingsR.status  === 'fulfilled') setHoldings(holdingsR.value.data.holdings || [])
    if (candidatesR.status === 'fulfilled') setCandidates(candidatesR.value.data.candidates || [])
    if (plansR.status     === 'fulfilled') {
      const plans = plansR.value.data.plans || []
      if (plans.length > 0) setPlan(plans[0])   // most recent
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Lazy-load score history when History tab opens
  const loadHistory = useCallback(async () => {
    if (historyLoaded) return
    try {
      const r = await rebalanceAPI.getHistoryAll(60)
      setHistoryMap(r.data.history || {})
      setHistoryLoaded(true)
    } catch (err) {
      toast.error('Failed to load score history')
    }
  }, [historyLoaded])

  // Lazy-load backtests when Backtest tab opens
  const loadBacktests = useCallback(async () => {
    try {
      const r = await rebalanceAPI.getBacktests()
      setBacktests(r.data.backtests || [])
      if (r.data.backtests?.[0] && !activeBacktest) {
        setActiveBacktest(r.data.backtests[0])
      }
    } catch {}
  }, [activeBacktest])

  // Lazy-load score-vs-return when Validation tab opens (or when window changes)
  const loadSvr = useCallback(async (win = svrWindow) => {
    try {
      const r = await rebalanceAPI.getScoreVsReturn(win)
      setSvr(r.data)
      setSvrLoaded(true)
    } catch { /* no data yet — that's fine */ }
  }, [svrWindow])

  const loadResearch = useCallback(async () => {
    try {
      const [mainR, rejR] = await Promise.allSettled([
        paperAPI.getResearch(),
        paperAPI.getRejectedAnalysis(),
      ])
      if (mainR.status === 'fulfilled') setResearchData(mainR.value.data)
      if (rejR.status  === 'fulfilled') setResearchRejected(rejR.value.data)
    } catch {}
    setResearchLoaded(true)
  }, [])

  const loadPaper = useCallback(async () => {
    try {
      const [statusR, cfgR, posR, tradesR, perfR] = await Promise.allSettled([
        paperAPI.getStatus(),
        paperAPI.getConfig(),
        paperAPI.getPositions(),
        paperAPI.getTrades({ limit: 20 }),
        paperAPI.getPerformance(),
      ])
      if (statusR.status === 'fulfilled') setPaperStatus(statusR.value.data)
      if (cfgR.status    === 'fulfilled') setPaperConfig(cfgR.value.data.config)
      if (posR.status    === 'fulfilled') setPaperPositions(posR.value.data.positions || [])
      if (tradesR.status === 'fulfilled') setPaperTrades(tradesR.value.data.trades || [])
      if (perfR.status   === 'fulfilled') setPaperPerf(perfR.value.data)
      setPaperLoaded(true)
    } catch {}
  }, [])

  useEffect(() => {
    if (tab === 'history')    loadHistory()
    if (tab === 'backtest')   loadBacktests()
    if (tab === 'validation') loadSvr(svrWindow)
    if (tab === 'paper')      loadPaper()
    if (tab === 'research')   loadResearch()
  }, [tab, loadHistory, loadBacktests, loadSvr, svrWindow, loadPaper, loadResearch])

  const handleSvrWindowChange = async (win) => {
    setSvrWindow(win)
    setSvrLoaded(false)
    await loadSvr(win)
  }

  const handleResolveForward = async () => {
    setResolvingFwd(true)
    try {
      await rebalanceAPI.resolveForwardReturns()
      toast.success('Resolution started (7d / 30d / 90d) — ~30–90 s. Refreshing shortly.')
      setSvrLoaded(false)
      setTimeout(async () => {
        try {
          const r = await rebalanceAPI.getScoreVsReturn(svrWindow)
          setSvr(r.data)
          setSvrLoaded(true)
        } catch {}
        setResolvingFwd(false)
      }, 90_000)
    } catch (err) {
      toast.error('Resolve failed: ' + (err.response?.data?.message || err.message))
      setResolvingFwd(false)
    }
  }

  // Trigger a backtest
  const handleRunBacktest = async () => {
    setRunningBacktest(true)
    try {
      const r = await rebalanceAPI.runBacktest(btConfig)
      toast.success(`Backtest started — ~${r.data.estimatedSeconds}s. Refresh in a moment.`)
      // Poll for completion
      setTimeout(async () => {
        await loadBacktests()
        setRunningBacktest(false)
      }, (r.data.estimatedSeconds || 30) * 1000 + 5000)
    } catch (err) {
      toast.error('Backtest failed: ' + (err.response?.data?.message || err.message))
      setRunningBacktest(false)
    }
  }

  // Score held stocks (background — returns quickly, data appears after polling)
  const handleScoreHoldings = async () => {
    setScoring(true)
    try {
      const r = await rebalanceAPI.scoreHoldings()
      toast.success(`Scoring ${r.data.symbolCount} holdings in background (~${r.data.estimatedSeconds}s)`)
      // Reload data after delay
      setTimeout(() => {
        rebalanceAPI.getHoldingsScored().then(r => setHoldings(r.data.holdings || []))
        setScoring(false)
      }, (r.data.estimatedSeconds || 30) * 1000 + 2000)
    } catch (err) {
      toast.error('Scoring failed: ' + (err.response?.data?.message || err.message))
      setScoring(false)
    }
  }

  // Score full Nifty 500 universe (very slow — background)
  const handleScoreUniverse = async () => {
    setScoringUniverse(true)
    try {
      const r = await rebalanceAPI.scoreUniverse()
      toast.success(`Universe scoring started — ~${r.data.estimatedMinutes} minutes. Come back later!`)
      setTimeout(() => setScoringUniverse(false), 5000)
    } catch (err) {
      toast.error('Universe scoring failed')
      setScoringUniverse(false)
    }
  }

  // Generate rebalance plan
  const handleGeneratePlan = async () => {
    setGenerating(true)
    try {
      const r = await rebalanceAPI.generatePlan()
      setPlan(r.data.plan)
      setTab('plan')
      toast.success('Rebalance plan generated')
    } catch (err) {
      toast.error('Plan generation failed: ' + (err.response?.data?.message || err.message))
    } finally {
      setGenerating(false)
    }
  }

  // Approve / cancel plan
  const handlePlanStatus = async (id, status) => {
    try {
      const r = await rebalanceAPI.updatePlanStatus(id, status)
      setPlan(r.data.plan)
      toast.success(`Plan ${status.toLowerCase()}`)
    } catch { toast.error('Failed to update plan') }
  }

  // Sorting + filtering for scorecard
  const SORT_KEY_MAP = {
    compositeScore: 'score.compositeScore',
    rank:           'score.rank',
    trendScore:     'score.trendScore',
    momentumScore:  'score.momentumScore',
    riskScore:      'score.riskScore',
    pnlPct:         'pnlPct',
    portfolioPct:   'portfolioPct',
  }

  function getNestedValue(obj, path) {
    return path.split('.').reduce((o, k) => o?.[k], obj) ?? (sortDir === 'asc' ? Infinity : -Infinity)
  }

  const filteredHoldings = holdings
    .filter(h => (!search || h.symbol.toLowerCase().includes(search.toLowerCase())) &&
                 (!sectorFilter || h.sector === sectorFilter))
    .sort((a, b) => {
      let va = getNestedValue(a, SORT_KEY_MAP[sortKey] || sortKey)
      let vb = getNestedValue(b, SORT_KEY_MAP[sortKey] || sortKey)
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
    })

  // Stable sort handlers for the module-level SortTh / CandTh / HistTh components
  const handleSortTh = (field) => {
    if (sortKey === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(field); setSortDir('asc') }
  }

  const handleCandSort = (key) => {
    if (candSortKey === key) setCandSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setCandSortKey(key); setCandSortDir('desc') }
  }

  const handleHistSort = (key) => {
    if (histSortKey === key) setHistSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setHistSortKey(key); setHistSortDir(key === 'symbol' ? 'asc' : 'asc') }
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner size="large" /></div>

  const scoredCount = holdings.filter(h => h.score).length

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-primary-500" /> Portfolio Rebalance
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Score-driven stock ranking &amp; rebalance recommendations — every decision is traceable
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleGeneratePlan} disabled={generating || scoredCount === 0}
            className="btn-primary flex items-center gap-2 text-sm">
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate Plan'}
          </button>
        </div>
      </div>

      {/* ── Status strip ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Holdings Scored', value: `${scoredCount}/${holdings.length}`, sub: scoredCount === 0 ? 'Click "Score Holdings"' : `${((scoredCount/Math.max(holdings.length,1))*100).toFixed(0)}% coverage` },
          { label: 'Universe Scored', value: `${status?.scoredSymbols ?? 0}`, sub: `of ${status?.universeSize ?? 501} Nifty 500` },
          { label: 'Regime', value: null, badge: status?.regime || 'UNKNOWN' },
          { label: 'Last Scored', value: status?.lastScoredAt ? new Date(status.lastScoredAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : 'Never', sub: status?.isStale ? '⚠ Stale data' : '' },
        ].map(({ label, value, sub, badge }) => (
          <div key={label} className="card py-3 px-4">
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            {badge ? <div className="mt-1"><RegimeBadge regime={badge} /></div>
              : <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>}
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
        ))}
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[['scorecard','Scorecard'], ['history','History'], ['candidates','Candidates'], ['plan','Plan'], ['backtest','Backtest'], ['validation','Validation'], ['paper','Paper Trading'], ['research','Research']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
            {id === 'plan' && plan && (
              <span className="ml-1.5 text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full">{plan.status}</span>
            )}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TAB: SCORECARD
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'scorecard' && (
        <div className="card p-0 overflow-hidden">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Holdings Scorecard</h3>
              <span className="text-xs text-gray-400">({filteredHoldings.length}{sectorFilter ? ` in ${sectorFilter}` : ''} holdings · sorted worst-first)</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Filter symbol…" className="input pl-8 py-1.5 text-sm w-32" />
              </div>
              {holdings.length > 0 && (
                <select
                  value={sectorFilter}
                  onChange={e => setSectorFilter(e.target.value)}
                  className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-300 text-gray-600 bg-white"
                >
                  <option value="">All sectors</option>
                  {[...new Set(holdings.map(h => h.sector).filter(Boolean))].sort().map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              <button onClick={handleScoreHoldings} disabled={scoring}
                className="flex items-center gap-1.5 text-sm border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600">
                <RefreshCw className={`w-3.5 h-3.5 ${scoring ? 'animate-spin' : ''}`} />
                {scoring ? 'Scoring…' : 'Score Holdings'}
              </button>
            </div>
          </div>

          {scoredCount === 0 && (
            <div className="px-4 py-8 text-center">
              <BarChart2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">No scores yet</p>
              <p className="text-gray-400 text-xs mt-1 mb-4">Fetch 1 year of price data from Yahoo Finance and compute scores.</p>
              <button onClick={handleScoreHoldings} disabled={scoring}
                className="btn-primary text-sm px-6 flex items-center gap-2 mx-auto">
                <RefreshCw className={`w-4 h-4 ${scoring ? 'animate-spin' : ''}`} />
                {scoring ? 'Scoring — please wait…' : 'Score My Holdings Now'}
              </button>
            </div>
          )}

          {scoredCount > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[960px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide">
                    <SortTh field="symbol"         label="Symbol"   sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Sector</th>
                    <SortTh field="compositeScore" label="Score"    right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="rank"           label="Rank"     right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="trendScore"     label="Trend"    right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="momentumScore"  label="Momentum" right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="riskScore"      label="Risk"     right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="pnlPct"         label="P&L%"     right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <SortTh field="portfolioPct"   label="% Book"   right sortKey={sortKey} sortDir={sortDir} onSort={handleSortTh} />
                    <th className="px-3 py-2.5 text-center font-medium text-gray-500">Decision</th>
                    <th className="px-3 py-2.5 text-center font-medium text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredHoldings.map(h => {
                    const s    = h.score
                    const cs   = s?.compositeScore
                    const plan_decision = plan?.decisions?.find(d => d.symbol === h.symbol)
                    const action = plan_decision?.action || (cs == null ? null : cs < 40 ? 'EXIT' : cs < 52 ? 'REDUCE' : cs >= 65 ? 'KEEP' : 'KEEP')
                    const isExp = expanded[h.symbol]
                    const pnlPos = (h.pnlPct || 0) >= 0

                    return (
                      <React.Fragment key={h.symbol}>
                        <tr className={`hover:bg-gray-50 transition-colors ${action === 'EXIT' ? 'bg-danger-50/30' : action === 'REDUCE' ? 'bg-amber-50/20' : ''}`}>
                          {/* Symbol */}
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => setExpanded(e => ({ ...e, [h.symbol]: !e[h.symbol] }))}
                                className="text-gray-300 hover:text-gray-500"
                              >
                                {isExp ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              </button>
                              <a href={`https://www.tradingview.com/chart/?symbol=NSE:${h.symbol}`} target="_blank" rel="noreferrer"
                                className="font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-0.5">
                                {h.symbol} <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                              </a>
                              <span className={`text-xs px-1 py-0.5 rounded ${h.instrumentType === 'ETF' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                {h.instrumentType || 'Stock'}
                              </span>
                            </div>
                          </td>
                          {/* Sector */}
                          <td className="px-3 py-2.5">
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">{h.sector || '—'}</span>
                          </td>
                          {/* Score */}
                          <td className="px-3 py-2.5">
                            {s ? <ScoreBar score={cs} /> : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                          {/* Rank */}
                          <td className="px-3 py-2.5 text-right text-sm font-medium text-gray-700">
                            {s?.rank ? `#${s.rank}` : '—'}
                          </td>
                          {/* Trend */}
                          <td className="px-3 py-2.5 text-right">
                            {s ? (
                              <span className={`text-xs font-semibold ${s.trendScore >= 65 ? 'text-success-600' : s.trendScore >= 45 ? 'text-amber-600' : 'text-danger-600'}`}>
                                {s.factors?.above50dma && s.factors?.above200dma ? '▲' : s.factors?.above50dma ? '→' : '▼'} {Math.round(s.trendScore)}
                              </span>
                            ) : '—'}
                          </td>
                          {/* Momentum */}
                          <td className="px-3 py-2.5 text-right">
                            {s?.factors ? (
                              <span className={`text-xs font-semibold ${s.momentumScore >= 65 ? 'text-success-600' : s.momentumScore >= 45 ? 'text-amber-600' : 'text-danger-600'}`}>
                                {(s.factors.rs6m >= 0 ? '+' : '')}{(s.factors.rs6m * 100).toFixed(1)}%
                              </span>
                            ) : '—'}
                          </td>
                          {/* Risk */}
                          <td className="px-3 py-2.5 text-right">
                            {s ? <span className={`text-xs font-semibold ${s.riskScore >= 65 ? 'text-success-600' : s.riskScore >= 45 ? 'text-amber-600' : 'text-danger-600'}`}>{Math.round(s.riskScore)}</span> : '—'}
                          </td>
                          {/* P&L% */}
                          <td className={`px-3 py-2.5 text-right text-sm font-medium ${pnlPos ? 'text-success-600' : 'text-danger-600'}`}>
                            {pnlPos ? '+' : ''}{fmt(h.pnlPct, 1)}%
                          </td>
                          {/* % Book */}
                          <td className="px-3 py-2.5 text-right text-sm text-gray-600">
                            {fmt(h.portfolioPct, 1)}%
                          </td>
                          {/* Decision */}
                          <td className="px-3 py-2.5 text-center">
                            {action ? <ActionBadge action={action} /> : <span className="text-gray-300 text-xs">Generate plan</span>}
                          </td>
                          {/* Action */}
                          <td className="px-3 py-2.5 text-center">
                            {(action === 'EXIT' || action === 'REDUCE') && (
                              <button
                                onClick={() => setOrderModal({
                                  isOpen: true,
                                  stock: { symbol: h.symbol, tradingsymbol: h.symbol, exchange: h.exchange || 'NSE' },
                                  action: 'SELL',
                                  qty: action === 'EXIT' ? h.totalQuantity : Math.ceil(h.totalQuantity * 0.5),
                                  price: s?.currentPrice || h.ltp || 0,
                                })}
                                className="text-xs bg-danger-100 text-danger-700 hover:bg-danger-200 px-2 py-1 rounded font-medium transition-colors"
                              >
                                {action === 'EXIT' ? 'Review Sell' : 'Review Trim'}
                              </button>
                            )}
                            {action === 'INCREASE' && (
                              <button
                                onClick={() => setOrderModal({
                                  isOpen: true,
                                  stock: { symbol: h.symbol, tradingsymbol: h.symbol, exchange: h.exchange || 'NSE' },
                                  action: 'BUY',
                                  qty: Math.max(1, Math.floor((h.totalValue * 0.1) / (s?.currentPrice || h.ltp || 1))),
                                  price: s?.currentPrice || h.ltp || 0,
                                })}
                                className="text-xs bg-success-100 text-success-700 hover:bg-success-200 px-2 py-1 rounded font-medium transition-colors"
                              >
                                Add More
                              </button>
                            )}
                          </td>
                        </tr>
                        {/* Expanded reasons */}
                        {isExp && (
                          <tr>
                            <td colSpan={11} className="px-8 py-3 bg-gray-50/80">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                {s && (
                                  <>
                                    {/* Factor attribution — each sub-score × its weight → contribution */}
                                    <div className="col-span-full mb-1">
                                      <p className="font-semibold text-gray-700 mb-2">Score attribution — how {cs?.toFixed(0)} was built</p>
                                      <div className="space-y-1.5">
                                        {[
                                          { label: 'Trend',     score: s.trendScore,     weight: s.weightsUsed?.trend     ?? 0.35, color: 'bg-blue-400' },
                                          { label: 'Momentum',  score: s.momentumScore,  weight: s.weightsUsed?.momentum  ?? 0.35, color: 'bg-purple-400' },
                                          { label: 'Risk',      score: s.riskScore,      weight: s.weightsUsed?.risk      ?? 0.25, color: 'bg-orange-400' },
                                          { label: 'Sentiment', score: s.sentimentScore, weight: s.weightsUsed?.sentiment ?? 0.05, color: 'bg-gray-400' },
                                        ].map(({ label, score, weight, color }) => {
                                          const contrib = (score ?? 0) * weight
                                          return (
                                            <div key={label} className="flex items-center gap-2">
                                              <span className="w-20 text-gray-500 shrink-0">{label} ({(weight * 100).toFixed(0)}%)</span>
                                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.min(score ?? 0, 100)}%` }} />
                                              </div>
                                              <span className="w-6 text-right text-gray-600 shrink-0">{(score ?? 0).toFixed(0)}</span>
                                              <span className="w-14 text-right text-gray-400 shrink-0">= {contrib.toFixed(1)} pts</span>
                                            </div>
                                          )
                                        })}
                                      </div>
                                      <p className="text-gray-400 mt-1.5">
                                        {s.dataQuality && <span className={`mr-2 px-1.5 py-0.5 rounded ${s.dataQuality === 'GOOD' ? 'bg-success-100 text-success-700' : s.dataQuality === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : 'bg-danger-100 text-danger-700'}`}>Data: {s.dataQuality} ({s.barsReceived} bars)</span>}
                                        Regime: {s.regime} · Scored: {s.date}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-700 mb-1">Trend factors</p>
                                      <ul className="space-y-0.5 text-gray-500">
                                        <li>Above 50-DMA: {s.factors?.above50dma ? '✅' : '❌'}</li>
                                        <li>Above 200-DMA: {s.factors?.above200dma ? '✅' : '❌'}</li>
                                        <li>Golden cross: {s.factors?.goldenCross ? '✅' : '❌'}</li>
                                        <li>From 52w high: {s.factors?.pctFromHigh52w?.toFixed(1)}%</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-700 mb-1">Momentum</p>
                                      <ul className="space-y-0.5 text-gray-500">
                                        <li>3m vs Nifty: {pct(s.factors?.rs3m * 100)}</li>
                                        <li>6m vs Nifty: {pct(s.factors?.rs6m * 100)}</li>
                                        <li>12m vs Nifty: {pct(s.factors?.rs12m * 100)}</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-700 mb-1">Risk</p>
                                      <ul className="space-y-0.5 text-gray-500">
                                        <li>1y volatility: {s.factors?.volatility252 != null ? (s.factors.volatility252 * 100).toFixed(0) + '%' : '—'}</li>
                                        <li>Max drawdown: {s.factors?.maxDrawdown252 != null ? (s.factors.maxDrawdown252 * 100).toFixed(0) + '%' : '—'}</li>
                                        <li>Beta: {s.factors?.beta60d?.toFixed(2) ?? '—'}</li>
                                      </ul>
                                    </div>
                                  </>
                                )}
                                {plan_decision?.reasons?.length > 0 && (
                                  <div className="col-span-full">
                                    <p className="font-semibold text-gray-700 mb-1">Plan decision ({plan_decision.action})</p>
                                    <ReasonsList reasons={plan_decision.reasons} />
                                    {plan_decision.taxNote && (
                                      <p className="text-amber-600 text-xs mt-1">⚠ Tax note: {plan_decision.taxNote}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: CANDIDATES
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'candidates' && (() => {
        // Sort + filter candidates locally
        const filteredCands = candidates
          .filter(c => (!candSearch || c.symbol.toUpperCase().includes(candSearch.toUpperCase())) &&
                       (!candSectorFilter || c.sector === candSectorFilter))
          .sort((a, b) => {
            let av, bv
            if (candSortKey === 'symbol')          { av = a.symbol;               bv = b.symbol }
            else if (candSortKey === 'rank')        { av = a.rank;                 bv = b.rank }
            else if (candSortKey === 'trendScore')  { av = a.trendScore ?? 0;      bv = b.trendScore ?? 0 }
            else if (candSortKey === 'rs6m')        { av = a.factors?.rs6m ?? 0;   bv = b.factors?.rs6m ?? 0 }
            else if (candSortKey === 'riskScore')   { av = a.riskScore ?? 0;       bv = b.riskScore ?? 0 }
            else if (candSortKey === 'currentPrice'){ av = a.currentPrice ?? 0;    bv = b.currentPrice ?? 0 }
            else                                    { av = a.compositeScore ?? 0;  bv = b.compositeScore ?? 0 }
            if (av === bv) return 0
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
            return candSortDir === 'asc' ? cmp : -cmp
          })
        return (
        <div className="card p-0 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-b border-gray-100 gap-2">
            <div>
              <h3 className="font-semibold text-gray-900">Top Replacement Candidates</h3>
              <p className="text-xs text-gray-400 mt-0.5">Nifty 500 stocks scoring &gt;60 not in your portfolio</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input value={candSearch} onChange={e => setCandSearch(e.target.value)}
                  placeholder="Filter symbol…"
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-300 w-32" />
              </div>
              {/* Sector filter — built from the live candidates list */}
              {candidates.length > 0 && (
                <select
                  value={candSectorFilter}
                  onChange={e => setCandSectorFilter(e.target.value)}
                  className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-300 text-gray-600 bg-white"
                >
                  <option value="">All sectors</option>
                  {[...new Set(candidates.map(c => c.sector).filter(Boolean))].sort().map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              <button onClick={handleScoreUniverse} disabled={scoringUniverse}
                className="flex items-center gap-1.5 text-sm border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600 shrink-0">
                <RefreshCw className={`w-3.5 h-3.5 ${scoringUniverse ? 'animate-spin' : ''}`} />
                {scoringUniverse ? 'Scoring…' : 'Score Universe'}
              </button>
            </div>
          </div>

          {candidates.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-gray-500 text-sm">No candidates yet. Score the universe first to rank all 500 Nifty stocks.</p>
              <button onClick={handleScoreUniverse} disabled={scoringUniverse}
                className="mt-4 btn-primary text-sm mx-auto flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${scoringUniverse ? 'animate-spin' : ''}`} />
                {scoringUniverse ? 'Scoring…' : 'Score All 500 Nifty Stocks'}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <CandTh field="symbol"        label="Symbol"      right={false} sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <th className="px-3 py-2.5 text-left font-medium whitespace-nowrap">Sector</th>
                    <CandTh field="compositeScore" label="Score"      sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <CandTh field="rank"           label="Rank"       sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <CandTh field="trendScore"     label="Trend"      sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <CandTh field="rs6m"           label="6m vs Nifty" sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <CandTh field="riskScore"      label="Risk"       sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <CandTh field="currentPrice"   label="Price"      sortKey={candSortKey} sortDir={candSortDir} onSort={handleCandSort} />
                    <th className="text-center px-3 py-2.5 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCands.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">No matches for "{candSearch}"</td></tr>
                  )}
                  {filteredCands.map(c => (
                    <tr key={c.symbol} className="hover:bg-success-50/20 transition-colors">
                      <td className="px-3 py-2.5">
                        <a href={`https://www.tradingview.com/chart/?symbol=NSE:${c.symbol}`} target="_blank" rel="noreferrer"
                          className="font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-0.5">
                          {c.symbol} <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                        </a>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">{c.sector || '—'}</span>
                      </td>
                      <td className="px-3 py-2.5 w-32">
                        <ScoreBar score={c.compositeScore} />
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium text-gray-700">#{c.rank}</td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`text-xs font-semibold ${c.trendScore >= 65 ? 'text-success-600' : c.trendScore >= 45 ? 'text-amber-600' : 'text-danger-600'}`}>
                          {c.factors?.above50dma ? '▲' : '▼'} {Math.round(c.trendScore || 0)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`text-xs font-semibold ${(c.factors?.rs6m || 0) >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          {(c.factors?.rs6m >= 0 ? '+' : '')}{((c.factors?.rs6m || 0) * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className={`text-xs font-semibold ${c.riskScore >= 65 ? 'text-success-600' : c.riskScore >= 45 ? 'text-amber-600' : 'text-danger-600'}`}>
                          {Math.round(c.riskScore || 0)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-700 font-medium">
                        {c.currentPrice ? `₹${fmt(c.currentPrice)}` : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => setOrderModal({
                            isOpen: true,
                            stock: { symbol: c.symbol, tradingsymbol: c.symbol, exchange: 'NSE' },
                            action: 'BUY',
                            qty: c.currentPrice ? Math.max(1, Math.floor(50000 / c.currentPrice)) : 1,
                            price: c.currentPrice || 0,
                          })}
                          className="flex items-center gap-1 text-xs bg-success-100 text-success-700 hover:bg-success-200 px-2.5 py-1 rounded font-medium transition-colors"
                        >
                          <ShoppingCart className="w-3 h-3" /> Buy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: PLAN
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'plan' && (
        <div className="space-y-5">
          {!plan ? (
            <div className="card text-center py-12">
              <Zap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No rebalance plan yet</p>
              <p className="text-gray-400 text-sm mt-1 mb-5">Score your holdings first, then generate a plan.</p>
              <button onClick={handleGeneratePlan} disabled={generating || scoredCount === 0}
                className="btn-primary text-sm mx-auto flex items-center gap-2">
                <Zap className="w-4 h-4" /> {generating ? 'Generating…' : 'Generate Rebalance Plan'}
              </button>
              {scoredCount === 0 && <p className="text-xs text-danger-600 mt-2">Score holdings first (Scorecard tab)</p>}
            </div>
          ) : (
            <>
              {/* Plan header */}
              <div className="card">
                {/* FAIL CLOSED: stale plan missing new diagnostic fields → prompt regeneration */}
                {(!plan.diagnostics || !plan.executionSequence?.length) && (
                  <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                    <div>
                      <span className="font-semibold">Plan is outdated</span> — diagnostics, execution sequence, and projected portfolio are missing from this plan.
                      Regenerate to unlock the full analysis.
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">Rebalance Plan</h3>
                    <RegimeBadge regime={plan.regime} />
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                      plan.status === 'DRAFT' ? 'bg-amber-100 text-amber-700 border-amber-200'
                        : plan.status === 'APPROVED' ? 'bg-success-100 text-success-700 border-success-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}>{plan.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {plan.status === 'DRAFT' && (
                      <>
                        <button onClick={() => handlePlanStatus(plan._id, 'APPROVED')}
                          className="text-sm btn-primary px-3 py-1.5">Approve Plan</button>
                        <button onClick={() => handlePlanStatus(plan._id, 'CANCELLED')}
                          className="text-sm border border-gray-200 px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                      </>
                    )}
                    <button onClick={handleGeneratePlan} disabled={generating}
                      className="text-sm border border-gray-200 px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1.5">
                      <RefreshCw className={`w-3.5 h-3.5 ${generating ? 'animate-spin' : ''}`} />
                      Regenerate
                    </button>
                  </div>
                </div>

                {/* Score simulation — before/after weighted avg composite score */}
                <div className="bg-gradient-to-r from-primary-50 to-success-50 rounded-xl p-4 mb-5">
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Portfolio Score Impact (If Plan Executed)</p>
                  <p className="text-xs text-gray-400 mb-3">Value-weighted average composite score across all holdings. Higher = better positioned portfolio.</p>
                  <div className="flex items-center justify-center gap-6 flex-wrap">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Avg Score Before</p>
                      <p className="text-3xl font-bold text-gray-700">
                        {(plan.simulation?.before?.weightedAvgStockScore ?? 0).toFixed(0)}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{plan.simulation?.before?.holdingsCount ?? 0} positions</p>
                    </div>
                    <div className="text-2xl text-gray-400">→</div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Avg Score After</p>
                      <p className="text-3xl font-bold text-success-600">
                        {(plan.simulation?.after?.weightedAvgStockScore ?? 0).toFixed(0)}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{plan.simulation?.after?.holdingsCount ?? 0} positions</p>
                    </div>
                    {(() => {
                      const delta = plan.simulation?.delta?.weightedAvgStockScore ?? 0
                      const pos = delta >= 0
                      return (
                        <div className="text-center border-l pl-6 ml-2">
                          <p className="text-xs text-gray-500">Score Delta</p>
                          <p className={`text-2xl font-bold ${pos ? 'text-success-600' : 'text-danger-600'}`}>
                            {pos ? '+' : ''}{delta.toFixed(1)} pts
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">projected improvement</p>
                        </div>
                      )
                    })()}
                  </div>
                </div>

                {/* Capital summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t pt-4">
                  <CapChip label="Capital Released" value={plan.summary?.capitalReleased} />
                  <CapChip label="Capital Required" value={plan.summary?.capitalDeployed} positive />
                  <CapChip label="Net Cashflow"
                    value={plan.summary?.netCashflow}
                    positive={(plan.summary?.netCashflow ?? 0) < 0} />
                  <CapChip label="Est. Costs" value={plan.summary?.estimatedCosts} />
                </div>

                {/* Counts */}
                <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t text-xs text-gray-500">
                  <span className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-danger-500" /> {plan.summary?.positionsExited} exits</span>
                  <span className="flex items-center gap-1"><ArrowDown className="w-3.5 h-3.5 text-amber-500" /> {plan.summary?.positionsTrimmed} trims</span>
                  <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-success-500" /> {plan.summary?.positionsKept} kept</span>
                  <span className="flex items-center gap-1"><ArrowUp className="w-3.5 h-3.5 text-blue-500" /> {plan.summary?.positionsAdded} new buys</span>
                </div>

                {/* Data quality warning — if many holdings unscored */}
                {plan.diagnostics?.scoreProfile?.unscoredCount > 0 && (
                  <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                      <span className="font-semibold">{plan.diagnostics.scoreProfile.unscoredCount} holding{plan.diagnostics.scoreProfile.unscoredCount !== 1 ? 's' : ''} without a score</span> — decisions for these default to KEEP.
                      Run "Score Holdings" then regenerate for full coverage.
                    </span>
                  </div>
                )}
              </div>

              {/* SELL TRADES */}
              {plan.sells?.length > 0 && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <ArrowDown className="w-4 h-4 text-danger-600" />
                    <h3 className="font-semibold text-gray-900">Sell / Trim</h3>
                    <span className="text-xs text-gray-400">({plan.sells.length})</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {plan.sells.map((s, i) => (
                      <div key={i} className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-danger-50/20">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{s.symbol}</span>
                            <ActionBadge action={s.action === 'SELL' ? (plan.decisions?.find(d => d.symbol === s.symbol)?.action === 'REDUCE' ? 'REDUCE' : 'EXIT') : s.action} />
                            <span className="text-xs text-gray-500">
                              {s.quantity} shares @ ₹{fmt(s.estimatedPrice)} = {fmtInr(s.estimatedValue)}
                            </span>
                            {s.taxNote && <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">⚠ {s.taxNote}</span>}
                          </div>
                          <ReasonsList reasons={s.reasons} />
                        </div>
                        <button
                          onClick={() => setOrderModal({
                            isOpen: true,
                            stock: { symbol: s.symbol, tradingsymbol: s.symbol, exchange: 'NSE' },
                            action: 'SELL', qty: s.quantity, price: s.estimatedPrice || 0,
                          })}
                          className="shrink-0 flex items-center gap-1 text-xs bg-danger-100 text-danger-700 hover:bg-danger-200 px-2.5 py-1.5 rounded font-medium transition-colors"
                        >
                          <ShoppingCart className="w-3 h-3" /> Review Sell
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* BUY TRADES */}
              {plan.buys?.length > 0 && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <ArrowUp className="w-4 h-4 text-success-600" />
                    <h3 className="font-semibold text-gray-900">Buy / Add</h3>
                    <span className="text-xs text-gray-400">({plan.buys.length})</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {plan.buys.map((b, i) => (
                      <div key={i} className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-success-50/20">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{b.symbol}</span>
                            <span className="text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded border border-success-200 font-semibold">BUY</span>
                            <span className="text-xs text-gray-500">
                              {b.quantity} shares @ ₹{fmt(b.estimatedPrice)} = {fmtInr(b.estimatedValue)}
                            </span>
                            {b.replacesSymbol && (
                              <span className="text-xs text-gray-400">← replaces {b.replacesSymbol}</span>
                            )}
                          </div>
                          <ReasonsList reasons={b.reasons} />
                        </div>
                        <button
                          onClick={() => setOrderModal({
                            isOpen: true,
                            stock: { symbol: b.symbol, tradingsymbol: b.symbol, exchange: 'NSE' },
                            action: 'BUY', qty: b.quantity, price: b.estimatedPrice || 0,
                          })}
                          className="shrink-0 flex items-center gap-1 text-xs bg-success-100 text-success-700 hover:bg-success-200 px-2.5 py-1.5 rounded font-medium transition-colors"
                        >
                          <ShoppingCart className="w-3 h-3" /> Review Buy
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── EXECUTION SEQUENCE ─────────────────────────────────────── */}
              {plan.executionSequence?.length > 0 && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary-500" />
                    <h3 className="font-semibold text-gray-900">Execution Sequence</h3>
                    <span className="text-xs text-gray-400">tax-aware ordering</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {plan.executionSequence.map((step, i) => (
                      <div key={i} className="px-4 py-4">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center mt-0.5">
                            {step.step}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">{step.phase}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                            <p className="text-xs text-gray-400 mt-1 italic">{step.rationale}</p>
                            {step.taxNote && (
                              <p className="text-xs text-amber-600 mt-1 bg-amber-50 px-2 py-1 rounded border border-amber-200">⚠ {step.taxNote}</p>
                            )}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {(step.orders || []).map((o, j) => (
                                <span key={j} className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  o.action === 'SELL'
                                    ? 'bg-danger-100 text-danger-700 border border-danger-200'
                                    : 'bg-success-100 text-success-700 border border-success-200'
                                }`}>
                                  {o.action} {o.symbol} ({o.quantity} @ {fmtInr(o.estimatedValue)})
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── PORTFOLIO DIAGNOSTICS ──────────────────────────────────── */}
              {plan.diagnostics && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary-500" />
                    <h3 className="font-semibold text-gray-900">Current Portfolio Diagnostics</h3>
                    <span className="text-xs text-gray-400">composition analysis at time of plan generation</span>
                  </div>
                  <div className="px-4 py-4 space-y-4">
                    {/* Concentration */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-gray-500">Concentration (HHI)</p>
                        <p className="text-lg font-bold text-gray-900">{(plan.diagnostics.concentration?.hhi * 100)?.toFixed(1)}%</p>
                        <p className="text-xs text-gray-400 mt-0.5">{plan.diagnostics.concentration?.hhiLabel}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-gray-500">Top 3 Positions</p>
                        <p className="text-lg font-bold text-gray-900">{plan.diagnostics.concentration?.top3Pct}%</p>
                        <p className="text-xs text-gray-400 mt-0.5">of portfolio value</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-gray-500">ETF Allocation</p>
                        <p className="text-lg font-bold text-gray-900">{plan.diagnostics.typeBreakdown?.etfPct}%</p>
                        <p className="text-xs text-gray-400 mt-0.5">{plan.diagnostics.typeBreakdown?.etfCount} ETF(s)</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-gray-500">Avg Score</p>
                        <p className={`text-lg font-bold ${(plan.diagnostics.scoreProfile?.avgCompositeScore ?? 0) >= 65 ? 'text-success-600' : (plan.diagnostics.scoreProfile?.avgCompositeScore ?? 0) >= 50 ? 'text-amber-600' : 'text-danger-600'}`}>
                          {plan.diagnostics.scoreProfile?.avgCompositeScore ?? '—'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{plan.diagnostics.scoreProfile?.scoredCount} scored holdings</p>
                      </div>
                    </div>

                    {/* Score buckets */}
                    {plan.diagnostics.scoreProfile?.buckets && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Score Distribution</p>
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          {[
                            { label: 'Strong (≥65)', count: plan.diagnostics.scoreProfile.buckets.strong,   color: 'bg-success-100 text-success-700 border-success-200' },
                            { label: 'Neutral (50–64)', count: plan.diagnostics.scoreProfile.buckets.neutral, color: 'bg-blue-100 text-blue-700 border-blue-200' },
                            { label: 'Weak (38–49)', count: plan.diagnostics.scoreProfile.buckets.weak,     color: 'bg-amber-100 text-amber-700 border-amber-200' },
                            { label: 'Exit zone (<38)', count: plan.diagnostics.scoreProfile.buckets.exitZone, color: 'bg-danger-100 text-danger-700 border-danger-200' },
                            { label: 'Unscored', count: plan.diagnostics.scoreProfile.buckets.unscored,  color: 'bg-gray-100 text-gray-500 border-gray-200' },
                          ].filter(b => b.count > 0).map(b => (
                            <span key={b.label} className={`px-2 py-1 rounded border font-medium ${b.color}`}>
                              {b.label}: {b.count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sector breakdown + concentration warning */}
                    {plan.diagnostics.sectors?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Sector Breakdown</p>
                        {/* Sector concentration warning — top sector >35% is a risk flag */}
                        {plan.diagnostics.sectors[0]?.pct > 35 && (
                          <div className="mb-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded px-2.5 py-2 text-xs text-amber-800">
                            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                            <span>
                              <strong>{plan.diagnostics.sectors[0].name}</strong> represents {plan.diagnostics.sectors[0].pct}% of portfolio value — sector concentration above 35% increases idiosyncratic risk.
                            </span>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          {plan.diagnostics.sectors.slice(0, 8).map(s => (
                            <div key={s.name} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 w-28 shrink-0 truncate">{s.name}</span>
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${s.pct > 35 ? 'bg-amber-400' : 'bg-primary-400'}`} style={{ width: `${Math.min(s.pct, 100)}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 w-12 text-right shrink-0">{s.pct}%</span>
                              <span className="text-xs text-gray-400 w-14 shrink-0">{s.count} holding{s.count !== 1 ? 's' : ''}</span>
                            </div>
                          ))}
                          {plan.diagnostics.sectors.length > 8 && (
                            <p className="text-xs text-gray-400 mt-1">+{plan.diagnostics.sectors.length - 8} more sectors</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── PROJECTED PORTFOLIO ────────────────────────────────────── */}
              {plan.projectedPortfolio && (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-success-600" />
                    <h3 className="font-semibold text-gray-900">Projected Portfolio</h3>
                    <span className="text-xs text-gray-400">if plan fully executed — estimates only</span>
                  </div>
                  <div className="px-4 py-4 space-y-4">
                    {/* Before/after KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Holdings Count',   before: plan.simulation?.before?.holdingsCount,  after: plan.projectedPortfolio.holdingsCount,    unit: '' },
                        { label: 'Avg Score',         before: plan.simulation?.before?.weightedAvgStockScore, after: plan.projectedPortfolio.weightedAvgScore, unit: '' },
                        { label: 'Concentration HHI', before: null, after: (plan.projectedPortfolio.hhi * 100)?.toFixed(1), unit: '%' },
                      ].map(({ label, before, after, unit }) => (
                        <div key={label} className="bg-gray-50 rounded-lg px-3 py-2.5">
                          <p className="text-xs text-gray-500">{label}</p>
                          <div className="flex items-baseline gap-2 mt-0.5">
                            {before != null && <span className="text-sm text-gray-400 line-through">{Number(before).toFixed(1)}{unit}</span>}
                            <span className="text-lg font-bold text-gray-900">{after}{unit}</span>
                          </div>
                        </div>
                      ))}
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-gray-500">Concentration Label</p>
                        <p className="text-sm font-bold text-gray-900 mt-0.5">{plan.projectedPortfolio.hhiLabel}</p>
                      </div>
                    </div>

                    {/* Changes */}
                    <div className="flex flex-wrap gap-3 text-xs">
                      {plan.projectedPortfolio.exitedPositions?.length > 0 && (
                        <div>
                          <p className="font-semibold text-danger-700 mb-1">Exited ({plan.projectedPortfolio.exitedPositions.length})</p>
                          <div className="flex flex-wrap gap-1">
                            {plan.projectedPortfolio.exitedPositions.map(s => (
                              <span key={s} className="bg-danger-50 text-danger-700 border border-danger-200 px-1.5 py-0.5 rounded">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {plan.projectedPortfolio.newPositions?.length > 0 && (
                        <div>
                          <p className="font-semibold text-success-700 mb-1">New positions ({plan.projectedPortfolio.newPositions.length})</p>
                          <div className="flex flex-wrap gap-1">
                            {plan.projectedPortfolio.newPositions.map(s => (
                              <span key={s} className="bg-success-50 text-success-700 border border-success-200 px-1.5 py-0.5 rounded">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Projected sector distribution */}
                    {plan.projectedPortfolio.sectors?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Projected Sector Distribution</p>
                        <div className="space-y-1.5">
                          {plan.projectedPortfolio.sectors.slice(0, 8).map(s => (
                            <div key={s.name} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 w-28 shrink-0 truncate">{s.name}</span>
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-success-400 rounded-full" style={{ width: `${Math.min(s.pct, 100)}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 w-12 text-right shrink-0">{s.pct}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-400 italic">
                      Projections are estimates based on current prices and approximate capital allocation. Actual results will vary based on execution prices, partial fills, and market movement.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: HISTORY — score trend per holding over last 60 days
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'history' && (() => {
        // Build flat rows for filter/sort
        const histRows = Object.entries(historyMap)
          .filter(([sym]) => !histSearch || sym.toUpperCase().includes(histSearch.toUpperCase()))
          .map(([sym, points]) => {
            if (!points.length) return null
            const latest = points[points.length - 1]
            const first  = points[0]
            const delta  = (latest.compositeScore ?? 0) - (first.compositeScore ?? 0)
            return { sym, points, latest, first, delta }
          })
          .filter(Boolean)
          .sort((a, b) => {
            let av, bv
            if      (histSortKey === 'symbol') { av = a.sym;   bv = b.sym }
            else if (histSortKey === 'delta')  { av = a.delta; bv = b.delta }
            else if (histSortKey === 'points') { av = a.points.length; bv = b.points.length }
            else /* latest */                  { av = a.latest.compositeScore ?? 0; bv = b.latest.compositeScore ?? 0 }
            if (av === bv) return 0
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
            return histSortDir === 'asc' ? cmp : -cmp
          })
        return (
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-primary-500" />
              <h3 className="font-semibold text-gray-900">Score Trend (last 60 days)</h3>
              <span className="text-xs text-gray-400">
                {historyLoaded ? `${Object.keys(historyMap).length} symbols` : 'Loading…'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input value={histSearch} onChange={e => setHistSearch(e.target.value)}
                  placeholder="Filter symbol…"
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-300 w-36" />
              </div>
              <button onClick={() => { setHistoryLoaded(false); loadHistory(); }}
                className="text-sm border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-1.5 shrink-0">
                <RefreshCw className="w-3.5 h-3.5" /> Reload
              </button>
            </div>
          </div>

          {!historyLoaded && (
            <div className="px-4 py-10 text-center"><LoadingSpinner /></div>
          )}

          {/* Honest footnote about what the delta means */}
          {historyLoaded && Object.keys(historyMap).length > 0 && (
            <div className="px-4 pt-2 pb-1 border-b border-gray-100">
              <p className="text-xs text-gray-400">
                <span className="font-medium text-gray-500">Δ vs Window Start</span> = latest score minus the <em>oldest available score</em> within the loaded 60-day window.
                Not necessarily exactly 30 days — depends on how many scoring runs have completed. Score history builds daily when "Score Holdings" is run.
              </p>
            </div>
          )}

          {historyLoaded && Object.keys(historyMap).length === 0 && (
            <div className="px-4 py-10 text-center">
              <p className="text-gray-500 text-sm">No score history yet.</p>
              <p className="text-gray-400 text-xs mt-1">Run "Score Holdings" daily to build the history.</p>
            </div>
          )}

          {historyLoaded && Object.keys(historyMap).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <HistTh field="symbol" label="Symbol"         right={false} sortKey={histSortKey} sortDir={histSortDir} onSort={handleHistSort} />
                    <HistTh field="latest" label="Latest Score"              sortKey={histSortKey} sortDir={histSortDir} onSort={handleHistSort} />
                    <HistTh field="delta"  label="Δ vs Window Start"         sortKey={histSortKey} sortDir={histSortDir} onSort={handleHistSort} />
                    <th className="px-3 py-2.5 text-left font-medium">Trend (60d)</th>
                    <HistTh field="points" label="Points"                    sortKey={histSortKey} sortDir={histSortDir} onSort={handleHistSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {histRows.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">No matches for "{histSearch}"</td></tr>
                  )}
                  {histRows.map(({ sym, points, latest, delta }) => {
                    const min   = Math.min(...points.map(p => p.compositeScore ?? 50))
                    const max   = Math.max(...points.map(p => p.compositeScore ?? 50))
                    const range = Math.max(max - min, 5)
                    return (
                      <tr key={sym} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-semibold text-primary-600">{sym}</td>
                        <td className="px-3 py-2.5 text-right w-40">
                          <ScoreBar score={latest.compositeScore} />
                        </td>
                        <td className={`px-3 py-2.5 text-right text-sm font-medium ${delta >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          {delta >= 0 ? '+' : ''}{delta.toFixed(0)}
                        </td>
                        <td className="px-3 py-2.5">
                          <svg width="120" height="32" className="overflow-visible">
                            <polyline fill="none"
                              stroke={delta >= 0 ? '#10b981' : '#ef4444'}
                              strokeWidth="1.5"
                              points={points.map((p, i) => {
                                const x = (i / Math.max(points.length - 1, 1)) * 120
                                const y = 32 - (((p.compositeScore ?? 50) - min) / range) * 28 - 2
                                return `${x},${y}`
                              }).join(' ')} />
                            <circle cx={120}
                              cy={32 - (((latest.compositeScore ?? 50) - min) / range) * 28 - 2}
                              r="2.5" fill={delta >= 0 ? '#10b981' : '#ef4444'} />
                          </svg>
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs text-gray-400">{points.length}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: BACKTEST — walk-forward strategy validation
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'backtest' && (
        <div className="space-y-5">
          {/* Honest caveats — FAIL CLOSED principle */}
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
            <div className="space-y-0.5">
              <p className="font-semibold">Backtest limitations — read before acting on results</p>
              <ul className="space-y-0.5 text-amber-700 list-none">
                <li>· <strong>Limited universe:</strong> Default uses top 50 Nifty500 (by order in JSON). Full 500-stock runs take ~3 min. Results on 50 stocks are indicative only.</li>
                <li>· <strong>Survivorship bias risk:</strong> Scores require 150+ price bars. Recently listed or delisted stocks may be underrepresented.</li>
                <li>· <strong>Look-ahead bias:</strong> Scoring uses 1 year of price history ending at the score date. Early months of a backtest use scores from a shorter window.</li>
                <li>· <strong>Transaction costs are modelled at 0.15% per side</strong> (brokerage + STT estimate). Actual costs may differ.</li>
                <li>· <strong>No slippage modelled.</strong> Monthly rebalancing assumes execution at closing price of rebalance day.</li>
              </ul>
            </div>
          </div>

          {/* Config + Run button */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary-500" /> Walk-Forward Backtest
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Validate the strategy: every month-end, pick top N by score, equal-weight, hold 1 month, repeat.
              Compares model returns to ^NSEI benchmark. Transaction costs applied at 0.15% per side.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">Start Date</label>
                <input type="date" value={btConfig.startDate}
                  onChange={e => setBtConfig(c => ({...c, startDate: e.target.value}))}
                  className="input py-1.5 text-sm w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">End Date</label>
                <input type="date" value={btConfig.endDate}
                  onChange={e => setBtConfig(c => ({...c, endDate: e.target.value}))}
                  className="input py-1.5 text-sm w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">Top N (portfolio size)</label>
                <input type="number" min="3" max="30" value={btConfig.topN}
                  onChange={e => setBtConfig(c => ({...c, topN: parseInt(e.target.value) || 5}))}
                  className="input py-1.5 text-sm w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">Initial Capital (₹)</label>
                <input type="number" min="100000" step="100000" value={btConfig.initialCapital}
                  onChange={e => setBtConfig(c => ({...c, initialCapital: parseInt(e.target.value) || 1000000}))}
                  className="input py-1.5 text-sm w-full" />
              </div>
            </div>
            <button onClick={handleRunBacktest} disabled={runningBacktest}
              className="btn-primary text-sm flex items-center gap-2">
              {runningBacktest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {runningBacktest ? 'Running backtest…' : 'Run Backtest'}
            </button>
          </div>

          {/* Backtest list selector */}
          {backtests.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Recent Backtests</h3>
              <div className="flex flex-wrap gap-2">
                {backtests.map(bt => (
                  <button key={bt._id}
                    onClick={() => setActiveBacktest(bt)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                      activeBacktest?._id === bt._id
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}>
                    {bt.config?.startDate} → {bt.config?.endDate}
                    <span className={`ml-1 ${bt.status === 'COMPLETE' ? 'text-success-600' : bt.status === 'FAILED' ? 'text-danger-600' : 'text-amber-600'}`}>
                      ({bt.status})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active backtest results */}
          {activeBacktest && activeBacktest.status === 'COMPLETE' && (
            <>
              {/* Summary metrics */}
              <div className="card">
                <div className="bg-gradient-to-r from-primary-50 to-success-50 rounded-xl p-4 mb-4">
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Strategy vs Benchmark</p>
                  <div className="flex items-center justify-center gap-6 flex-wrap">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Model Return</p>
                      <p className={`text-3xl font-bold ${(activeBacktest.metrics?.totalReturn ?? 0) >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                        {(activeBacktest.metrics?.totalReturn ?? 0) >= 0 ? '+' : ''}{(activeBacktest.metrics?.totalReturn ?? 0).toFixed(1)}%
                      </p>
                    </div>
                    <div className="text-2xl text-gray-400">vs</div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">^NSEI Benchmark</p>
                      <p className={`text-3xl font-bold ${(activeBacktest.metrics?.benchmarkReturn ?? 0) >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                        {(activeBacktest.metrics?.benchmarkReturn ?? 0) >= 0 ? '+' : ''}{(activeBacktest.metrics?.benchmarkReturn ?? 0).toFixed(1)}%
                      </p>
                    </div>
                    <div className="text-center border-l pl-6 ml-2">
                      <p className="text-xs text-gray-500">Excess Return (Alpha)</p>
                      <p className={`text-2xl font-bold ${(activeBacktest.metrics?.excessReturn ?? 0) >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                        {(activeBacktest.metrics?.excessReturn ?? 0) >= 0 ? '+' : ''}{(activeBacktest.metrics?.excessReturn ?? 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t pt-4">
                  <div>
                    <p className="text-xs text-gray-500">CAGR</p>
                    <p className="text-lg font-semibold">{(activeBacktest.metrics?.annualisedReturn ?? 0).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sharpe (rf=7%)</p>
                    <p className="text-lg font-semibold">{(activeBacktest.metrics?.sharpeRatio ?? 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Max Drawdown</p>
                    <p className="text-lg font-semibold text-danger-600">-{(activeBacktest.metrics?.maxDrawdown ?? 0).toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Win Rate vs Benchmark</p>
                    <p className="text-lg font-semibold">{(activeBacktest.metrics?.winRate ?? 0).toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Months Run</p>
                    <p className="text-lg font-semibold">{activeBacktest.metrics?.monthsRun ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Avg Turnover</p>
                    <p className="text-lg font-semibold">{(activeBacktest.metrics?.avgTurnover ?? 0).toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Transaction Costs</p>
                    <p className="text-lg font-semibold">{fmtInr(activeBacktest.metrics?.totalCosts)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Annualised Vol</p>
                    <p className="text-lg font-semibold">{(activeBacktest.metrics?.annualisedVol ?? 0).toFixed(1)}%</p>
                  </div>
                </div>
              </div>

              {/* Monthly returns table */}
              <div className="card p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">Monthly Detail</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2.5 text-left font-medium">Month</th>
                        <th className="px-3 py-2.5 text-right font-medium">Portfolio Value</th>
                        <th className="px-3 py-2.5 text-right font-medium">Month Return</th>
                        <th className="px-3 py-2.5 text-right font-medium">Cumulative</th>
                        <th className="px-3 py-2.5 text-right font-medium">^NSEI Cum</th>
                        <th className="px-3 py-2.5 text-right font-medium">Turnover</th>
                        <th className="px-3 py-2.5 text-left font-medium">Picks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activeBacktest.monthly?.map((m, i) => {
                        const beat = m.monthReturn > 0;
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-700">{m.date}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmtInr(m.portfolioValue)}</td>
                            <td className={`px-3 py-2 text-right font-medium ${beat ? 'text-success-600' : 'text-danger-600'}`}>
                              {beat ? '+' : ''}{m.monthReturn.toFixed(2)}%
                            </td>
                            <td className={`px-3 py-2 text-right font-semibold ${m.cumulativeReturn >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                              {m.cumulativeReturn >= 0 ? '+' : ''}{m.cumulativeReturn.toFixed(2)}%
                            </td>
                            <td className={`px-3 py-2 text-right ${m.benchmarkReturn >= 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                              {m.benchmarkReturn >= 0 ? '+' : ''}{m.benchmarkReturn.toFixed(2)}%
                            </td>
                            <td className="px-3 py-2 text-right text-gray-500">{m.turnoverPct?.toFixed(0)}%</td>
                            <td className="px-3 py-2 text-xs text-gray-500">{m.selectedSymbols?.join(', ')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeBacktest && activeBacktest.status === 'RUNNING' && (
            <div className="card text-center py-10">
              <RefreshCw className="w-8 h-8 text-primary-500 mx-auto mb-3 animate-spin" />
              <p className="text-gray-600">Backtest running…</p>
              <p className="text-xs text-gray-400 mt-1">Refreshing automatically when done.</p>
            </div>
          )}

          {activeBacktest && activeBacktest.status === 'FAILED' && (
            <div className="card text-center py-8 bg-danger-50">
              <XCircle className="w-8 h-8 text-danger-500 mx-auto mb-3" />
              <p className="text-danger-700 font-medium">Backtest failed</p>
              <p className="text-xs text-danger-600 mt-1">{activeBacktest.error}</p>
            </div>
          )}

          {backtests.length === 0 && !runningBacktest && (
            <div className="card text-center py-10">
              <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No backtests run yet</p>
              <p className="text-gray-400 text-sm mt-1">Configure parameters above and click "Run Backtest" to validate the strategy.</p>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          VALIDATION TAB — Score vs Forward Return (Prediction Tracking)
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'validation' && (
        <div className="space-y-5">

          {/* Header + actions */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900">Score Prediction Validation (IC Analysis)</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Spearman IC (Information Coefficient) measures rank correlation between composite score and actual forward return.
                IC = 0 means the score is random noise. IC &gt; 0.05 is weak signal. IC &gt; 0.15 is considered strong in quantitative research.
                Requires ≥ 30 observations for statistical significance — anything below that is accumulating data.
              </p>
            </div>
            <button
              onClick={handleResolveForward}
              disabled={resolvingFwd}
              className="btn-secondary flex items-center gap-2 text-sm shrink-0"
            >
              {resolvingFwd
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Resolving…</>
                : <><RefreshCw className="w-4 h-4" /> Resolve Forward Returns</>}
            </button>
          </div>

          {/* ── Window switcher ──────────────────────────────────────────── */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 font-medium">Forward window:</span>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {[7, 30, 90].map(w => (
                <button key={w}
                  onClick={() => handleSvrWindowChange(w)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    svrWindow === w ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {w}d
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 ml-4">
              {[['buckets','4 Buckets'], ['deciles','10 Deciles']].map(([v, label]) => (
                <button key={v}
                  onClick={() => setSvrView(v)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    svrView === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Statistical significance guard */}
          {svr && svr.observationCount > 0 && svr.observationCount < 30 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
              <div>
                <span className="font-semibold">Insufficient observations for statistical significance</span> — {svr.observationCount} resolved so far.
                IC values below 30 observations are unreliable and should not be used for strategy decisions.
                Keep scoring daily; results become meaningful at 30+ observations.
              </div>
            </div>
          )}

          {/* ── IC Comparison — all 3 windows side by side ───────────────── */}
          {svr?.windowSummary && svr.observationCount >= 5 && (
            <div className="grid grid-cols-3 gap-3">
              {[['ret7','7-day','News / short-term'], ['ret30','30-day','Core momentum'], ['ret90','90-day','Trend / rotation']].map(([key, label, note]) => {
                const ws = svr.windowSummary[key]
                const ic = ws?.ic
                const isActive = (svrWindow === 7 && key === 'ret7') || (svrWindow === 30 && key === 'ret30') || (svrWindow === 90 && key === 'ret90')
                return (
                  <div key={key} className={`card px-4 py-4 border-2 transition-all cursor-pointer ${
                    isActive
                      ? ic > 0.15  ? 'border-success-400 bg-success-50'
                        : ic > 0.05 ? 'border-amber-300 bg-amber-50'
                        : ic != null && ic <= 0 ? 'border-danger-200 bg-danger-50'
                        : 'border-primary-300 bg-primary-50'
                      : 'border-gray-100 hover:border-gray-200'
                  }`}
                    onClick={() => handleSvrWindowChange(key === 'ret7' ? 7 : key === 'ret90' ? 90 : 30)}>
                    <p className="text-xs text-gray-500 font-medium">{label}</p>
                    <p className="text-xs text-gray-400 mb-2">{note}</p>
                    <p className={`text-2xl font-black ${
                      ic === null    ? 'text-gray-300'
                      : ic > 0.15   ? 'text-success-700'
                      : ic > 0.05   ? 'text-amber-700'
                      : ic > 0      ? 'text-gray-600'
                      : 'text-danger-700'
                    }`}>{ic != null ? ic.toFixed(3) : '—'}</p>
                    <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${
                        ic > 0.15 ? 'bg-success-500' : ic > 0.05 ? 'bg-amber-400' : ic > 0 ? 'bg-gray-400' : 'bg-danger-400'
                      }`} style={{ width: `${ic != null ? clamp((ic / 0.30) * 100, 0, 100) : 0}%` }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                      {ws?.n ?? 0} obs {(ws?.n ?? 0) < 30 ? '· ⚠ below 30 threshold' : ''}
                    </p>
                  </div>
                )
              })}
            </div>
          )}
          {svr?.windowSummary && (
            <p className="text-xs text-gray-400 italic">
              IC benchmarks: &lt;0.05 = noise · 0.05–0.15 = weak signal · &gt;0.15 = strong signal (quant research standard).
              Positive IC across all windows suggests the scoring model has predictive value at this holding period.
            </p>
          )}

          {/* IC interpretation for active window */}
          {svr && svr.observationCount >= 5 && (
            <div className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${
              svr.icInterpretation?.includes('Strong') ? 'bg-success-50 text-success-800'
              : svr.icInterpretation?.includes('Moderate') ? 'bg-amber-50 text-amber-800'
              : svr.icInterpretation?.includes('Negative') ? 'bg-danger-50 text-danger-800'
              : 'bg-gray-50 text-gray-700'
            }`}>
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">{svrWindow}d IC: </span>{svr.icInterpretation}
                {svr.message && <span className="block text-xs mt-0.5 opacity-80">⏳ {svr.message}</span>}
                {svr.correlationCaveat && (
                  <span className="block text-xs mt-0.5 opacity-60">⚠ {svr.correlationCaveat}</span>
                )}
              </div>
            </div>
          )}

          {/* ── Factor IC breakdown + Regime IC ────────────────────────────── */}
          {svr?.factorIC && svr.observationCount >= 5 && Object.keys(svr.factorIC).length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Factor IC table */}
              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-800">Factor-Level IC — {svrWindow}d</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Which factor actually drives the predictive power?
                    IC near zero = that factor is noise at this window.
                  </p>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-4 py-2 font-medium">Factor</th>
                      <th className="text-right px-4 py-2 font-medium">Weight</th>
                      <th className="text-right px-4 py-2 font-medium">IC</th>
                      <th className="px-4 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {['composite','trend','momentum','risk','sentiment'].map(key => {
                      const f = svr.factorIC[key]
                      if (!f) return null
                      const ic = f.ic
                      const hasIC = ic != null
                      return (
                        <tr key={key} className={`hover:bg-gray-50 ${key === 'composite' ? 'bg-gray-50/60 font-semibold' : ''}`}>
                          <td className="px-4 py-2.5 text-gray-900">{f.label}</td>
                          <td className="px-4 py-2.5 text-right text-gray-400 text-xs">{f.weight ?? '—'}</td>
                          <td className={`px-4 py-2.5 text-right font-mono font-semibold ${
                            !hasIC      ? 'text-gray-300'
                            : ic > 0.15 ? 'text-success-700'
                            : ic > 0.05 ? 'text-amber-700'
                            : ic > 0    ? 'text-gray-600'
                            : 'text-danger-700'
                          }`}>
                            {hasIC ? ic.toFixed(3) : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            {hasIC && (
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${
                                  ic > 0.15 ? 'bg-success-500' : ic > 0.05 ? 'bg-amber-400'
                                  : ic > 0  ? 'bg-gray-400'   : 'bg-danger-400'
                                }`} style={{ width: `${clamp(Math.abs(ic) / 0.30 * 100, 0, 100)}%` }} />
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Regime IC table */}
              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-800">Regime-Stratified IC — {svrWindow}d</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Many factors work in one regime and fail in another.
                    Regime detected at scoring time, so this is retrospective.
                  </p>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-4 py-2 font-medium">Regime</th>
                      <th className="text-right px-4 py-2 font-medium">Obs</th>
                      <th className="text-right px-4 py-2 font-medium">IC</th>
                      <th className="text-left px-4 py-2 font-medium">Signal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {['BULL','BEAR','SIDEWAYS'].map(regime => {
                      const r = svr.regimeIC?.[regime]
                      if (!r) return null
                      const ic = r.ic
                      const EMOJI = { BULL: '🟢', BEAR: '🔴', SIDEWAYS: '🟡' }
                      return (
                        <tr key={regime} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {EMOJI[regime]} {regime}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{r.n}</td>
                          <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${
                            ic === null  ? 'text-gray-300'
                            : ic > 0.15  ? 'text-success-700'
                            : ic > 0.05  ? 'text-amber-700'
                            : ic > 0     ? 'text-gray-600'
                            : 'text-danger-700'
                          }`}>
                            {ic != null ? ic.toFixed(3) : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {r.n < 5 ? 'Too few obs' : r.interpretation?.split('—')[0]?.trim()}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    💡 Regime detected live by SMA50 vs SMA200 at scoring time. Captured in every ScoreHistory row.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Bucket / Decile table ─────────────────────────────────────── */}
          {svr && (svrView === 'buckets' ? svr.buckets : svr.deciles)?.length > 0 && (() => {
            const rows = svrView === 'buckets' ? svr.buckets : svr.deciles
            const maxAbs = Math.max(...rows.map(r => Math.abs(r.avgReturn ?? 0)), 0.1)
            return (
              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      {svrView === 'buckets' ? '4-Bucket Analysis' : '10-Decile Analysis'} — {svrWindow}-day forward return
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {svrView === 'deciles'
                        ? 'A good model shows a monotonically increasing slope from bottom-left to top-right.'
                        : 'A good model shows avg return decreasing from top bucket to bottom.'}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">{svr.observationCount} total obs</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-4 py-2.5 font-medium">
                        {svrView === 'deciles' ? 'Decile' : 'Score Bucket'}
                      </th>
                      <th className="text-right px-4 py-2.5 font-medium">n</th>
                      <th className="text-right px-4 py-2.5 font-medium">Avg Return</th>
                      <th className="text-right px-4 py-2.5 font-medium">Median</th>
                      <th className="text-right px-4 py-2.5 font-medium">Win%</th>
                      <th className="px-4 py-2.5 w-32"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((b) => {
                      const a = b.avgReturn
                      const hasData = b.count > 0 && a !== null
                      return (
                        <tr key={b.label} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 font-semibold text-gray-900">{b.label}</td>
                          <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">{b.count}</td>
                          <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${
                            !hasData ? 'text-gray-300' : a > 0 ? 'text-success-700' : 'text-danger-700'
                          }`}>{hasData ? pct(a) : '—'}</td>
                          <td className={`px-4 py-2.5 text-right tabular-nums ${
                            !hasData ? 'text-gray-300' : b.medianReturn > 0 ? 'text-success-600' : 'text-danger-600'
                          }`}>{hasData ? pct(b.medianReturn) : '—'}</td>
                          <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">
                            {b.winRate !== null ? `${b.winRate}%` : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            {hasData && (
                              <div className="flex items-center h-5">
                                {/* Centered zero-line bar */}
                                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden relative">
                                  {a >= 0
                                    ? <div className="absolute left-1/2 top-0 h-full bg-success-400 rounded-r-full"
                                        style={{ width: `${clamp((a / maxAbs) * 50, 0, 50)}%` }} />
                                    : <div className="absolute right-1/2 top-0 h-full bg-danger-400 rounded-l-full"
                                        style={{ width: `${clamp((Math.abs(a) / maxAbs) * 50, 0, 50)}%` }} />
                                  }
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          })()}

          {/* ── Top gains / losses ──────────────────────────────────────────── */}
          {svr?.topGains?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: 'Best Predictions', icon: TrendingUp, iconColor: 'text-success-500', data: svr.topGains, retColor: 'text-success-700', prefix: '+' },
                { title: 'Worst Predictions', icon: TrendingDown, iconColor: 'text-danger-500', data: svr.topLosses, retColor: 'text-danger-700', prefix: '' },
              ].map(({ title, icon: Icon, iconColor, data, retColor, prefix }) => (
                <div key={title} className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                    <h3 className="text-sm font-semibold text-gray-800">{title} ({svrWindow}d)</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-400 uppercase">
                        <th className="text-left px-4 py-2 font-medium">Symbol</th>
                        <th className="text-right px-4 py-2 font-medium">Score</th>
                        <th className="text-right px-4 py-2 font-medium">Return</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">
                            {r.symbol}
                            <span className="ml-1.5 text-xs text-gray-400">{r.date}</span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreBg(+r.score)}`}>{r.score}</span>
                          </td>
                          <td className={`px-4 py-2 text-right font-semibold ${retColor}`}>
                            {prefix}{r.return}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          {/* ── Empty / accumulating states ─────────────────────────────────── */}
          {!svr && (
            <div className="card text-center py-12">
              <ShieldCheck className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No validation data yet</p>
              <p className="text-gray-400 text-sm mt-1 max-w-md mx-auto">
                Score history is building. After {svrWindow}+ days of daily scoring the resolver
                (auto-runs 06:10 IST) will fill forward returns and the IC will appear.
              </p>
            </div>
          )}
          {svr && svr.observationCount < 5 && (
            <div className="card text-center py-10">
              <Activity className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Accumulating observations</p>
              <p className="text-gray-400 text-sm mt-1 max-w-md mx-auto">{svr.message}</p>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: PAPER TRADING
          Temporary host in Rebalance. Future: dedicated Trading module.
          Business logic is entirely in /api/paper/* + paperTradingEngine.js.
          Only rendering lives here — zero business logic in this component.
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'paper' && (
        <div className="space-y-5">

          {/* Paper trading caveat */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
            <div>
              <span className="font-semibold">Simulated capital only — no real orders placed.</span>
              {' '}P&L figures, win rates, and cycle returns are paper performance on virtual ₹5L.
              Past paper performance does not guarantee real-money results. Slippage, market impact, and KYC constraints are not modelled.
            </div>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-primary-500" /> Paper Trading Engine
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Autonomous swing trading (3–5 days) on simulated capital. Real market data, simulated fills.
                Runs at 06:15 IST daily after scoring.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={loadPaper} className="btn-secondary text-sm flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
              <button
                onClick={async () => {
                  setPaperCycleRunning(true)
                  try {
                    await paperAPI.runCycle()
                    toast.success('Morning cycle started (~15s)')
                    setTimeout(() => { loadPaper(); setPaperCycleRunning(false) }, 18000)
                  } catch (e) { toast.error(e.response?.data?.message || e.message); setPaperCycleRunning(false) }
                }}
                disabled={paperCycleRunning}
                className="btn-primary text-sm flex items-center gap-1.5"
              >
                {paperCycleRunning
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Running…</>
                  : <><Play className="w-3.5 h-3.5" /> Run Cycle Now</>}
              </button>
            </div>
          </div>

          {!paperLoaded && <div className="card py-10 text-center"><LoadingSpinner /></div>}

          {paperLoaded && paperStatus && (() => {
            const s = paperStatus
            const statusColor = {
              ACTIVE: 'bg-success-100 text-success-700',
              PAUSED: 'bg-amber-100 text-amber-700',
              KILLED: 'bg-danger-100 text-danger-700',
              IDLE:   'bg-gray-100 text-gray-600',
              DISABLED: 'bg-gray-100 text-gray-400',
            }[s.engineStatus] || 'bg-gray-100 text-gray-500'

            const pnlColor = (v) => !v ? 'text-gray-500' : v > 0 ? 'text-success-700' : 'text-danger-700'

            return (<>

              {/* Kill switch banner */}
              {s.killSwitch && (
                <div className="flex items-center gap-3 bg-danger-50 border border-danger-200 rounded-lg px-4 py-3">
                  <Power className="w-5 h-5 text-danger-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-danger-800">Kill Switch Active</p>
                    <p className="text-xs text-danger-600">{s.killSwitchReason}</p>
                  </div>
                  <button
                    onClick={async () => {
                      await paperAPI.setKillSwitch(false)
                      toast.success('Kill switch disengaged')
                      loadPaper()
                    }}
                    className="text-xs px-3 py-1.5 bg-danger-600 text-white rounded font-medium hover:bg-danger-700"
                  >
                    Disengage
                  </button>
                </div>
              )}

              {/* Status strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Engine',        value: null,               badge: s.engineStatus, badgeColor: statusColor },
                  { label: 'Regime',        value: null,               badge: s.currentRegime, badgeColor: { BULL:'bg-success-100 text-success-700', BEAR:'bg-danger-100 text-danger-700', SIDEWAYS:'bg-amber-100 text-amber-700' }[s.currentRegime] || 'bg-gray-100 text-gray-500' },
                  { label: 'Open Positions', value: `${s.openPositions} / ${paperConfig?.maxPositions || 7}` },
                  { label: 'Drawdown',       value: `${((s.currentDrawdown || 0) * 100).toFixed(1)}%`, danger: s.currentDrawdown > 0.10 },
                ].map(({ label, value, badge, badgeColor, danger }) => (
                  <div key={label} className="card py-3 px-4">
                    <p className="text-xs text-gray-500 font-medium">{label}</p>
                    {badge
                      ? <span className={`mt-1 inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>{badge}</span>
                      : <p className={`text-xl font-bold mt-0.5 ${danger ? 'text-danger-600' : 'text-gray-900'}`}>{value}</p>}
                  </div>
                ))}
              </div>

              {/* Capital summary */}
              <div className="card px-5 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Capital</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Strategy Capital', value: `₹${fmt(s.strategyCapital, 0)}` },
                    { label: 'Cash',             value: `₹${fmt(s.cashBalance, 0)}` },
                    { label: 'Invested',         value: `₹${fmt(s.investedValue, 0)}` },
                    { label: 'Exposure',         value: `${((s.exposure || 0) * 100).toFixed(0)}%` },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className="text-base font-bold text-gray-900 mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
                {/* Exposure bar */}
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary-400 rounded-full"
                    style={{ width: `${clamp((s.exposure || 0) * 100, 0, 100)}%` }} />
                </div>

                {/* P&L row */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                  {[
                    { label: 'Realized P&L',   value: s.realizedPnL },
                    { label: 'Unrealized P&L', value: s.unrealizedPnL },
                    { label: 'Total P&L',      value: s.totalPnL },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-xs text-gray-400">{label}</p>
                      <p className={`text-base font-bold mt-0.5 ${pnlColor(value)}`}>
                        {value != null ? `${value >= 0 ? '+' : ''}₹${fmt(Math.abs(value), 0)}` : '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance summary (from /performance endpoint) */}
              {paperPerf?.summary && (() => {
                const p = paperPerf.summary
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Win Rate',      value: p.winRate != null ? `${(p.winRate * 100).toFixed(1)}%` : '—' },
                      { label: 'Profit Factor', value: p.profitFactor != null ? p.profitFactor.toFixed(2) : '—' },
                      { label: 'Avg Hold',      value: p.avgHoldDays != null ? `${p.avgHoldDays.toFixed(1)}d` : '—' },
                      { label: 'Trades',        value: p.totalTradesClosed ?? 0 },
                    ].map(({ label, value }) => (
                      <div key={label} className="card py-3 px-4">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
                      </div>
                    ))}
                  </div>
                )
              })()}

              {/* Kill switch button */}
              {!s.killSwitch && (
                <div className="flex justify-end">
                  <button
                    onClick={async () => {
                      if (!window.confirm('Engage kill switch? This halts all new paper trades until manually disengaged.')) return
                      await paperAPI.setKillSwitch(true, 'Manual halt')
                      toast.success('Kill switch engaged')
                      loadPaper()
                    }}
                    className="flex items-center gap-2 text-sm text-danger-600 border border-danger-200 px-3 py-1.5 rounded-lg hover:bg-danger-50">
                    <Power className="w-3.5 h-3.5" /> Engage Kill Switch
                  </button>
                </div>
              )}

              {/* Open Positions */}
              {paperPositions.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">Open Positions ({paperPositions.length})</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="text-left px-4 py-2 font-medium">Symbol</th>
                        <th className="text-right px-4 py-2 font-medium">Qty</th>
                        <th className="text-right px-4 py-2 font-medium">Entry</th>
                        <th className="text-right px-4 py-2 font-medium">Score</th>
                        <th className="text-right px-4 py-2 font-medium">MTM P&L</th>
                        <th className="text-right px-4 py-2 font-medium">Days</th>
                        <th className="text-left px-4 py-2 font-medium">Top Factor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {paperPositions.map(p => {
                        const latestMtm = p.mtmHistory?.[p.mtmHistory.length - 1]
                        const holdDays  = Math.round((Date.now() - new Date(p.entryDate)) / 86400000)
                        const mtmPnLPct = latestMtm?.mtmPnLPct
                        return (
                          <tr key={p._id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-semibold text-primary-600">{p.symbol}</td>
                            <td className="px-4 py-2.5 text-right text-gray-700">{p.qty}</td>
                            <td className="px-4 py-2.5 text-right text-gray-700">₹{fmt(p.entryFill?.fillPrice)}</td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreBg(p.scoreAtEntry)}`}>
                                {p.scoreAtEntry?.toFixed(0) || '—'}
                              </span>
                            </td>
                            <td className={`px-4 py-2.5 text-right font-semibold ${mtmPnLPct >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
                              {mtmPnLPct != null ? `${mtmPnLPct >= 0 ? '+' : ''}${mtmPnLPct.toFixed(2)}%` : '—'}
                            </td>
                            <td className={`px-4 py-2.5 text-right tabular-nums ${holdDays >= (paperConfig?.maxHoldDays || 5) - 1 ? 'text-amber-600 font-semibold' : 'text-gray-600'}`}>
                              {holdDays}d
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-500 capitalize">{p.topEntryFactor || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {paperPositions.length === 0 && (
                <div className="card text-center py-8">
                  <DollarSign className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No open positions</p>
                  <p className="text-gray-400 text-sm mt-1">Engine holds cash when no valid opportunities pass risk checks.</p>
                </div>
              )}

              {/* Recent closed trades */}
              {paperTrades.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-800">Recent Closed Trades</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Net P&L after all fees (brokerage, STT, SEBI, GST)</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="text-left px-4 py-2 font-medium">Symbol</th>
                        <th className="text-right px-4 py-2 font-medium">Entry Score</th>
                        <th className="text-right px-4 py-2 font-medium">Exit Score</th>
                        <th className="text-right px-4 py-2 font-medium">Net P&L</th>
                        <th className="text-right px-4 py-2 font-medium">Hold</th>
                        <th className="text-left px-4 py-2 font-medium">Exit Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {paperTrades.map(t => (
                        <tr key={t._id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-semibold text-gray-900">{t.symbol}</td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreBg(t.scoreAtEntry)}`}>
                              {t.scoreAtEntry?.toFixed(0) || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreBg(t.scoreAtExit)}`}>
                              {t.scoreAtExit?.toFixed(0) || '—'}
                            </span>
                          </td>
                          <td className={`px-4 py-2.5 text-right font-semibold ${t.netPnL >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
                            {t.netPnL != null ? `${t.netPnL >= 0 ? '+' : ''}₹${fmt(Math.abs(t.netPnL), 0)}` : '—'}
                            {t.netPnLPct != null && (
                              <span className="block text-xs font-normal opacity-70">
                                {t.netPnLPct >= 0 ? '+' : ''}{t.netPnLPct.toFixed(2)}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{t.holdDays}d</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">
                            {t.exitReason?.replace(/_/g, ' ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </>)
          })()}

          {paperLoaded && !paperStatus && (
            <div className="card text-center py-12">
              <Cpu className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Paper trading not initialised</p>
              <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">
                Click "Run Cycle Now" to start. The engine will auto-initialise with ₹5L strategy capital.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: RESEARCH PLATFORM
          Phase 3 — Observability, Attribution, Learning & Research.
          Computes all analytics from closed PaperTrade documents.
          Zero business logic here — all computation is in /api/paper/research.
          Future: migrate to a top-level Research module (no backend changes needed).
          ════════════════════════════════════════════════════════════════════ */}
      {tab === 'research' && (() => {
        const d  = researchData
        const rj = researchRejected
        const n  = d?.meta?.tradeCount ?? 0

        // Inline format helpers scoped to research
        const wr   = v => v == null ? '—' : `${(v * 100).toFixed(1)}%`
        const pf   = v => v == null ? '—' : (isFinite(v) ? v.toFixed(2) : '∞')
        const ret  = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
        const alp  = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
        const inr  = v => v == null ? '—' : `${v >= 0 ? '+' : '−'}₹${fmt(Math.abs(v), 0)}`
        const cn   = v => v == null ? 'text-gray-400' : v > 0 ? 'text-success-700' : v < 0 ? 'text-danger-700' : 'text-gray-500'
        const icC  = ic => ic == null ? 'text-gray-400' : ic > 0.10 ? 'text-success-700 font-semibold' : ic > 0 ? 'text-amber-600' : 'text-danger-600'

        // Sub-tab definitions
        const RESEARCH_TABS = [
          ['overview',  'Overview'],
          ['attribution','Attribution'],
          ['factors',   'Factors'],
          ['regimes',   'Regimes'],
          ['sectors',   'Sectors'],
          ['periods',   'Hold Periods'],
          ['deciles',   'Deciles'],
          ['rejected',  'Rejected'],
          ['exit',      'Exit Quality'],
          ['readiness', 'Readiness'],
        ]

        return (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart className="w-4 h-4 text-primary-500" /> Research Platform
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Empirical strategy analysis. Every table answers a question about what works and why.
                </p>
              </div>
              <button onClick={loadResearch} className="btn-secondary text-sm flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* Sub-tab strip */}
            <div className="flex gap-0.5 overflow-x-auto border-b border-gray-200 pb-0 -mb-0 no-scrollbar">
              {RESEARCH_TABS.map(([id, label]) => (
                <button key={id} onClick={() => setResearchSub(id)}
                  className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${
                    researchSub === id
                      ? 'border-primary-500 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >{label}</button>
              ))}
            </div>

            {/* Loading */}
            {!researchLoaded && <div className="card py-10 text-center"><LoadingSpinner /></div>}

            {/* No trades yet */}
            {researchLoaded && n === 0 && (
              <div className="card text-center py-12">
                <BarChart className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No closed trades yet</p>
                <p className="text-gray-400 text-sm mt-1 max-w-sm mx-auto">
                  Research data populates as paper trades close. Run the engine via the Paper Trading tab.
                </p>
              </div>
            )}

            {/* ── OVERVIEW ── */}
            {researchLoaded && n > 0 && researchSub === 'overview' && (() => {
              const ov = d.overview
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Closed Trades',  value: n },
                      { label: 'Win Rate',       value: wr(ov.winRate) },
                      { label: 'Profit Factor',  value: pf(ov.profitFactor) },
                      { label: 'Expectancy',     value: inr(ov.expectancy) },
                      { label: 'Avg Alpha',      value: alp(ov.avgAlpha), color: cn(ov.avgAlpha) },
                      { label: 'Avg Return',     value: ret(ov.avgReturn), color: cn(ov.avgReturn) },
                      { label: 'Total Net P&L',  value: inr(ov.totalNetPnL), color: cn(ov.totalNetPnL) },
                      { label: 'Total Fees',     value: ov.totalFees != null ? `₹${fmt(ov.totalFees, 0)}` : '—' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="card py-3 px-4">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className={`text-xl font-bold mt-0.5 ${color || 'text-gray-900'}`}>{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Strategy versions */}
                  {d.meta?.strategyVersions && Object.keys(d.meta.strategyVersions).length > 0 && (
                    <div className="card px-4 py-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trade History by Strategy Version</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(d.meta.strategyVersions).map(([ver, cnt]) => (
                          <span key={ver} className="text-xs bg-primary-50 text-primary-700 border border-primary-100 rounded px-2 py-1 font-medium">
                            {ver} — {cnt} trade{cnt !== 1 ? 's' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Readiness summary */}
                  {d.readiness && (
                    <div className="card px-4 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Readiness Summary</p>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d.readiness.graduationReady ? 'bg-success-100 text-success-700' : 'bg-amber-100 text-amber-700'}`}>
                          {d.readiness.score} checks passed
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(d.readiness.checks).map(([key, c]) => (
                          <div key={key} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${c.pass ? 'bg-success-50 text-success-700' : 'bg-gray-50 text-gray-500'}`}>
                            {c.pass ? <CheckCircle className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0 text-gray-400" />}
                            <span>{c.label}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-3">{d.readiness.note}</p>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* ── ATTRIBUTION (per-trade table) ── */}
            {researchLoaded && n > 0 && researchSub === 'attribution' && (() => {
              const trades = d.recentTrades || []
              return (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-800">Trade Attribution — Last {trades.length}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Regime, sector, score delta, and alpha per closed trade</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                          {['Symbol','Sector','Entry Score','Exit Score','Δ Score','Hold','Return','Alpha','Regime','Exit Reason'].map(h => (
                            <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {trades.map(t => (
                          <tr key={t._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-semibold text-primary-700">{t.symbol}</td>
                            <td className="px-3 py-2 text-gray-500">{t.sector || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded font-bold ${scoreBg(t.scoreAtEntry)}`}>
                                {t.scoreAtEntry?.toFixed(0) ?? '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded font-bold ${scoreBg(t.scoreAtExit)}`}>
                                {t.scoreAtExit?.toFixed(0) ?? '—'}
                              </span>
                            </td>
                            <td className={`px-3 py-2 font-semibold ${cn(t.scoreDelta)}`}>
                              {t.scoreDelta != null ? `${t.scoreDelta >= 0 ? '+' : ''}${t.scoreDelta.toFixed(0)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{t.holdDays != null ? `${t.holdDays}d` : '—'}</td>
                            <td className={`px-3 py-2 font-semibold ${cn(t.netPnLPct)}`}>{ret(t.netPnLPct)}</td>
                            <td className={`px-3 py-2 font-semibold ${cn(t.alphaPct)}`}>{alp(t.alphaPct)}</td>
                            <td className="px-3 py-2">
                              {t.regimeAtEntry && (
                                <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                  { BULL: 'bg-success-100 text-success-700', BEAR: 'bg-danger-100 text-danger-700', SIDEWAYS: 'bg-amber-100 text-amber-700' }[t.regimeAtEntry] || 'bg-gray-100 text-gray-500'
                                }`}>{t.regimeAtEntry}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500">{t.exitReason?.replace(/_/g, ' ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })()}

            {/* ── FACTORS ── */}
            {researchLoaded && n > 0 && researchSub === 'factors' && (() => {
              const f = d.factors || {}
              return (
                <div className="space-y-4">
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Factor Predictive Power (Spearman IC)</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Spearman rank correlation between entry sub-score and realised trade P&L%. IC &lt;0.05 = noise · 0.05–0.15 = weak · &gt;0.15 = strong. Based on closed paper trades only.</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-4 py-2 font-medium">Factor</th>
                          <th className="text-right px-4 py-2 font-medium">IC</th>
                          <th className="text-right px-4 py-2 font-medium">Avg Winner Score</th>
                          <th className="text-right px-4 py-2 font-medium">Avg Loser Score</th>
                          <th className="text-right px-4 py-2 font-medium">Spread</th>
                          <th className="text-right px-4 py-2 font-medium">n</th>
                          <th className="text-left px-4 py-2 font-medium">Signal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {Object.entries(f).map(([name, fd]) => (
                          <tr key={name} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium capitalize text-gray-800">{name}</td>
                            <td className={`px-4 py-3 text-right font-mono text-base ${icC(fd.ic)}`}>
                              {fd.ic != null ? fd.ic.toFixed(3) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-success-700 font-medium">
                              {fd.avgWinner?.toFixed(1) ?? '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-danger-700 font-medium">
                              {fd.avgLoser?.toFixed(1) ?? '—'}
                            </td>
                            <td className={`px-4 py-3 text-right font-semibold ${fd.spread > 5 ? 'text-success-700' : fd.spread > 0 ? 'text-amber-600' : fd.spread != null ? 'text-danger-600' : 'text-gray-400'}`}>
                              {fd.spread != null ? `${fd.spread >= 0 ? '+' : ''}${fd.spread.toFixed(1)}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-400">{fd.n ?? 0}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{fd.interpretation || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="card px-4 py-3 text-xs text-gray-500 space-y-1">
                    <p className="font-semibold text-gray-700">Interpreting IC</p>
                    <p><span className="font-medium text-success-700">IC {'>'} 0.15</span> — Strong: genuine predictive power. Factor genuinely discriminates.</p>
                    <p><span className="font-medium text-amber-600">0.05–0.15</span> — Moderate: signal exists, keep scoring. Monitor across more trades.</p>
                    <p><span className="font-medium text-gray-600">0–0.05</span> — Weak/noise: marginal edge, not yet reliable.</p>
                    <p><span className="font-medium text-danger-600">{'<'} 0</span> — Negative: factor may be hurting returns. Investigate construction.</p>
                    <p className="text-gray-400 mt-1">Spread (winner score − loser score): a large positive spread confirms the factor discriminates, even if IC is low due to outliers.</p>
                  </div>
                </div>
              )
            })()}

            {/* ── REGIMES ── */}
            {researchLoaded && n > 0 && researchSub === 'regimes' && (() => {
              const regimes = d.regimes || []
              const regimeColor = { BULL: 'bg-success-100 text-success-700', BEAR: 'bg-danger-100 text-danger-700', SIDEWAYS: 'bg-amber-100 text-amber-700', UNKNOWN: 'bg-gray-100 text-gray-500' }
              return (
                <div className="space-y-4">
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Performance by Market Regime</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Regime detected at entry time (SMA50 vs SMA200). Answers: where does the strategy work?</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                          {['Regime','Trades','Win Rate','Profit Factor','Expectancy','Avg Return','Avg Alpha'].map(h => (
                            <th key={h} className={`px-4 py-2 font-medium ${h === 'Regime' ? 'text-left' : 'text-right'}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {regimes.map(r => (
                          <tr key={r.regime} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${regimeColor[r.regime] || 'bg-gray-100 text-gray-500'}`}>{r.regime}</span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 font-medium">{r.count}</td>
                            <td className="px-4 py-3 text-right font-semibold" style={{ color: r.winRate > 0.5 ? '#15803d' : r.winRate < 0.4 ? '#dc2626' : '#92400e' }}>{wr(r.winRate)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn((r.profitFactor ?? 0) - 1)}`}>{pf(r.profitFactor)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn(r.expectancy)}`}>{inr(r.expectancy)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn(r.avgReturn)}`}>{ret(r.avgReturn)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn(r.avgAlpha)}`}>{alp(r.avgAlpha)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">Key question</p>
                    <p>If BULL regime significantly outperforms BEAR/SIDEWAYS, consider <strong>suspending new entries during non-BULL periods</strong> (already configurable via <code>allowNewEntriesInBear</code>). If all regimes show similar win rates, the strategy is regime-independent.</p>
                  </div>
                </div>
              )
            })()}

            {/* ── SECTORS ── */}
            {researchLoaded && n > 0 && researchSub === 'sectors' && (() => {
              const sectors = d.sectors || []
              return (
                <div className="card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-800">Performance by Sector</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Which sectors contribute most to returns? Sorted by trade count.</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        {['Sector','Trades','Win Rate','Profit Factor','Expectancy','Avg Return','Avg Alpha'].map(h => (
                          <th key={h} className={`px-4 py-2 font-medium ${h === 'Sector' ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {sectors.map(s => (
                        <tr key={s.sector} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-800">{s.sector}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{s.count}</td>
                          <td className="px-4 py-3 text-right font-semibold" style={{ color: s.winRate > 0.5 ? '#15803d' : s.winRate < 0.4 ? '#dc2626' : '#92400e' }}>{wr(s.winRate)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${cn((s.profitFactor ?? 0) - 1)}`}>{pf(s.profitFactor)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${cn(s.expectancy)}`}>{inr(s.expectancy)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${cn(s.avgReturn)}`}>{ret(s.avgReturn)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${cn(s.avgAlpha)}`}>{alp(s.avgAlpha)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })()}

            {/* ── HOLDING PERIODS ── */}
            {researchLoaded && n > 0 && researchSub === 'periods' && (() => {
              const periods = d.holdingPeriods || []
              return (
                <div className="space-y-4">
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Performance by Holding Period</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Should winners be held longer? Are exits too early or too late?</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                          {['Period','Trades','Win Rate','Profit Factor','Expectancy','Avg Return'].map(h => (
                            <th key={h} className={`px-4 py-2 font-medium ${h === 'Period' ? 'text-left' : 'text-right'}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {periods.map(p => (
                          <tr key={p.label} className={`hover:bg-gray-50 ${p.label === '3d' || p.label === '4d' || p.label === '5d' ? 'bg-primary-50/30' : ''}`}>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {p.label}
                              {(p.label === '3d' || p.label === '4d' || p.label === '5d') && (
                                <span className="ml-2 text-xs text-primary-500 font-normal">target window</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700">{p.count}</td>
                            <td className="px-4 py-3 text-right font-semibold" style={{ color: p.winRate > 0.5 ? '#15803d' : p.winRate < 0.4 ? '#dc2626' : '#92400e' }}>{wr(p.winRate)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn((p.profitFactor ?? 0) - 1)}`}>{pf(p.profitFactor)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn(p.expectancy)}`}>{inr(p.expectancy)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${cn(p.avgReturn)}`}>{ret(p.avgReturn)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">What to look for</p>
                    <p>If 6–7d and 8–10d rows show higher avg returns than 3–5d, <strong>winners are being cut too early</strong> — consider raising <code>maxHoldDays</code>. If 1–2d rows show negative returns, quick exits are correct. The 3–5d highlighted rows are the current target window (configurable).</p>
                  </div>
                </div>
              )
            })()}

            {/* ── SCORE DECILES ── */}
            {researchLoaded && n > 0 && researchSub === 'deciles' && (() => {
              const deciles = (d.deciles || []).filter(d => d.count > 0)
              const maxCount = Math.max(...deciles.map(d => d.count), 1)
              return (
                <div className="space-y-4">
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Returns by Entry Score Decile</h3>
                      <p className="text-xs text-gray-400 mt-0.5">The core validation: a healthy strategy shows monotonically better returns at higher scores.</p>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-4 py-2 font-medium">Score Range</th>
                          <th className="text-right px-4 py-2 font-medium">Trades</th>
                          <th className="text-right px-4 py-2 font-medium">Win Rate</th>
                          <th className="text-right px-4 py-2 font-medium">Avg Return</th>
                          <th className="text-right px-4 py-2 font-medium">Avg Alpha</th>
                          <th className="px-4 py-2 font-medium" style={{ width: '120px' }}>Visual</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {deciles.map(dec => {
                          const barWidth = Math.round(dec.count / maxCount * 100)
                          const retNorm  = clamp(((dec.avgReturn ?? 0) + 5) / 10 * 100, 0, 100)
                          const isGreen  = (dec.avgReturn ?? 0) >= 0
                          return (
                            <tr key={dec.label} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-800">{dec.label}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{dec.count}</td>
                              <td className="px-4 py-3 text-right font-semibold" style={{ color: dec.winRate > 0.5 ? '#15803d' : dec.winRate < 0.4 ? '#dc2626' : '#92400e' }}>{wr(dec.winRate)}</td>
                              <td className={`px-4 py-3 text-right font-semibold ${cn(dec.avgReturn)}`}>{ret(dec.avgReturn)}</td>
                              <td className={`px-4 py-3 text-right font-semibold ${cn(dec.avgAlpha)}`}>{alp(dec.avgAlpha)}</td>
                              <td className="px-4 py-3">
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${retNorm}%`, backgroundColor: isGreen ? '#22c55e' : '#ef4444' }} />
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">Expected pattern</p>
                    <p>Higher score deciles should show higher avg returns and win rates. A <strong>monotonic slope</strong> from bottom-left to top-right confirms the score is predictive. A flat or inverted pattern means the composite needs recalibration.</p>
                  </div>
                </div>
              )
            })()}

            {/* ── REJECTED SIGNALS ── */}
            {researchLoaded && researchSub === 'rejected' && (() => {
              const summary = rj?.summary || []
              const recent  = rj?.recent  || []
              const verdictColor = v => v?.includes('blocking winners') ? 'text-danger-600' : v?.includes('validated') ? 'text-success-700' : 'text-gray-500'
              return (
                <div className="space-y-4">
                  {/* Summary by rule */}
                  <div className="card overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-800">Rejected Signal Analysis — By Risk Rule</h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Which rules save money vs block good trades? Positive avg return = rule blocked profitable trades.
                        {rj?.resolved != null && ` ${rj.resolved}/${rj.total} signals have 20d forward returns.`}
                      </p>
                    </div>
                    {summary.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">No rejected signals yet. Run paper trading cycles to populate.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                            {['Rule','Blocked','Avg Ret (5d)','Avg Ret (20d)','Win% (20d)','Verdict'].map(h => (
                              <th key={h} className={`px-4 py-2 font-medium ${h === 'Rule' || h === 'Verdict' ? 'text-left' : 'text-right'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {summary.map(s => (
                            <tr key={s.rejectionCode} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.rejectionCode}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{s.count}</td>
                              <td className={`px-4 py-3 text-right font-semibold ${cn(s.avgRet5d)}`}>
                                {s.avgRet5d != null ? ret(s.avgRet5d) : '—'}
                              </td>
                              <td className={`px-4 py-3 text-right font-semibold ${cn(s.avgRet20d)}`}>
                                {s.avgRet20d != null ? ret(s.avgRet20d) : '—'}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-600">
                                {s.wouldWinPct20d != null ? `${s.wouldWinPct20d}%` : '—'}
                              </td>
                              <td className={`px-4 py-3 text-sm font-medium ${verdictColor(s.verdict)}`}>
                                {s.resolvedCount > 0 ? s.verdict : <span className="text-gray-300">Pending data</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Recent individual rejections */}
                  {recent.length > 0 && (
                    <div className="card overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800">Recent Rejected Signals</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                              {['Date','Symbol','Rule','Score','Regime','Ret 5d','Ret 20d'].map(h => (
                                <th key={h} className={`px-3 py-2 font-medium ${h === 'Date' || h === 'Symbol' || h === 'Rule' || h === 'Regime' ? 'text-left' : 'text-right'}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {recent.map(r => (
                              <tr key={r._id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-500">{r.date}</td>
                                <td className="px-3 py-2 font-semibold text-gray-800">{r.symbol}</td>
                                <td className="px-3 py-2 font-mono text-gray-600">{r.rejectionCode}</td>
                                <td className="px-3 py-2">
                                  {r.compositeScore != null && (
                                    <span className={`px-1.5 py-0.5 rounded font-bold ${scoreBg(r.compositeScore)}`}>
                                      {r.compositeScore.toFixed(0)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {r.regime && (
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${{ BULL: 'bg-success-100 text-success-700', BEAR: 'bg-danger-100 text-danger-700', SIDEWAYS: 'bg-amber-100 text-amber-700' }[r.regime] || 'bg-gray-100 text-gray-500'}`}>{r.regime}</span>
                                  )}
                                </td>
                                <td className={`px-3 py-2 text-right font-semibold ${cn(r.ret5)}`}>
                                  {r.ret5 != null ? ret(r.ret5) : <span className="text-gray-300">pending</span>}
                                </td>
                                <td className={`px-3 py-2 text-right font-semibold ${cn(r.ret20)}`}>
                                  {r.ret20 != null ? ret(r.ret20) : <span className="text-gray-300">pending</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">How to read this</p>
                    <p>Forward returns are filled daily at 06:10 IST. <strong>Negative avg return</strong> = the rule correctly blocked a losing trade (keep the rule). <strong>Positive avg return</strong> = the rule blocked a winner (consider relaxing). Requires ≥ 5 resolved signals per rule for statistical meaning.</p>
                  </div>
                </div>
              )
            })()}

            {/* ── EXIT QUALITY ── */}
            {researchLoaded && n > 0 && researchSub === 'exit' && (() => {
              const eq = d.exitQuality || {}
              if (eq.n === 0) return (
                <div className="card text-center py-10 text-gray-400 text-sm">{eq.message || 'No exit quality data yet.'}</div>
              )
              return (
                <div className="space-y-4">
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Avg MFE',         value: eq.avgMFE != null ? `+${eq.avgMFE.toFixed(2)}%` : '—', sub: 'Max favorable excursion', color: 'text-success-700' },
                      { label: 'Avg MAE',          value: eq.avgMAE != null ? `−${eq.avgMAE.toFixed(2)}%` : '—', sub: 'Max adverse excursion', color: 'text-danger-700' },
                      { label: 'Avg Capture',      value: eq.avgCapture != null ? `${(eq.avgCapture * 100).toFixed(0)}%` : '—', sub: 'Of best move captured' },
                      { label: 'Left on Table',    value: eq.avgLeftOnTable != null ? `${eq.avgLeftOnTable.toFixed(2)}%` : '—', sub: 'Avg return missed', color: cn(-(eq.avgLeftOnTable ?? 0)) },
                    ].map(({ label, value, sub, color }) => (
                      <div key={label} className="card py-3 px-4">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className={`text-xl font-bold mt-0.5 ${color || 'text-gray-900'}`}>{value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Exit type summary */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Near-Peak Exits',   value: eq.peakExitCount,  sub: '≥ 80% MFE captured', color: 'text-success-700' },
                      { label: 'Early Exits',        value: eq.earlyExitCount, sub: '< 50% MFE captured',  color: 'text-amber-600' },
                      { label: 'Loss Outcomes',      value: eq.lossCount,      sub: 'Finished negative',    color: 'text-danger-700' },
                    ].map(({ label, value, sub, color }) => (
                      <div key={label} className="card py-3 px-4">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className={`text-2xl font-bold mt-0.5 ${color}`}>{value ?? '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* By exit reason */}
                  {eq.byReason && eq.byReason.length > 0 && (
                    <div className="card overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-800">Exit Quality by Trigger</h3>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                            {['Exit Reason','n','Avg MFE','Avg MAE','Avg Capture','Avg Return'].map(h => (
                              <th key={h} className={`px-4 py-2 font-medium ${h === 'Exit Reason' ? 'text-left' : 'text-right'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {eq.byReason.map(r => (
                            <tr key={r.reason} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-xs font-mono text-gray-700">{r.reason?.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{r.count}</td>
                              <td className="px-4 py-3 text-right text-success-700">{r.avgMFE != null ? `+${r.avgMFE.toFixed(2)}%` : '—'}</td>
                              <td className="px-4 py-3 text-right text-danger-700">{r.avgMAE != null ? `−${r.avgMAE.toFixed(2)}%` : '—'}</td>
                              <td className="px-4 py-3 text-right text-gray-700">
                                {r.avgCapture != null ? `${(r.avgCapture * 100).toFixed(0)}%` : '—'}
                              </td>
                              <td className={`px-4 py-3 text-right font-semibold ${cn(r.avgReturn)}`}>{ret(r.avgReturn)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">Definitions</p>
                    <p><strong>MFE</strong> — highest MTM value reached during the trade (best unrealised return). <strong>MAE</strong> — lowest MTM value (worst drawdown while in position). <strong>Capture</strong> — actual exit return ÷ MFE. A low capture means the trade reversed after the peak, suggesting the exit signal was too slow.</p>
                  </div>
                </div>
              )
            })()}

            {/* ── READINESS ── */}
            {researchLoaded && researchSub === 'readiness' && (() => {
              const rd = d?.readiness
              if (!rd) return <div className="card text-center py-10 text-gray-400 text-sm">No data yet.</div>
              return (
                <div className="space-y-4">
                  {/* Headline */}
                  <div className={`card px-5 py-5 border-2 ${rd.graduationReady ? 'border-success-300 bg-success-50' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`text-lg font-bold ${rd.graduationReady ? 'text-success-800' : 'text-gray-700'}`}>
                          {rd.graduationReady ? '✅ Strategy is Graduation-Ready' : '⏳ Insufficient Evidence'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">{rd.note}</p>
                      </div>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${rd.graduationReady ? 'bg-success-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {rd.score}
                      </span>
                    </div>
                  </div>

                  {/* Individual checks */}
                  <div className="space-y-2">
                    {Object.entries(rd.checks).map(([key, c]) => (
                      <div key={key} className={`card px-4 py-3 flex items-center gap-4 ${c.pass ? 'border-l-4 border-l-success-500' : 'border-l-4 border-l-gray-200'}`}>
                        <div className="shrink-0">
                          {c.pass
                            ? <CheckCircle className="w-5 h-5 text-success-600" />
                            : <XCircle className="w-5 h-5 text-gray-300" />}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${c.pass ? 'text-success-800' : 'text-gray-600'}`}>{c.label}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Current: <strong className={c.pass ? 'text-success-700' : 'text-gray-700'}>
                              {c.value ?? '—'}{typeof c.required === 'number' && key === 'drawdown' ? '%' : ''}
                            </strong>
                            {' '}/ Required: <strong>{c.required}</strong>
                            {key === 'drawdown' ? '%' : key === 'profitFactor' ? 'x' : key === 'tradeCount' ? ' trades' : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="card px-4 py-3 text-xs text-gray-500">
                    <p className="font-semibold text-gray-700 mb-1">What "graduation" means</p>
                    <p>These thresholds (50 trades, PF {'>'}1.25, DD {'<'}10%, positive alpha, positive expectancy, IC {'≥'}0.05) are the minimum evidence bar before expanded testing. Passing all checks does NOT automatically enable live trading — that requires a separate explicit consent flow. The purpose of paper trading is to accumulate this evidence.</p>
                  </div>
                </div>
              )
            })()}

          </div>
        )
      })()}

      {/* ── Order Modal ──────────────────────────────────────────────────────── */}
      <OrderModal
        isOpen={orderModal.isOpen}
        onClose={() => setOrderModal(m => ({ ...m, isOpen: false }))}
        stock={orderModal.stock}
        orderType={orderModal.action}
        ltp={orderModal.price}
        initialQuantity={orderModal.qty}
        initialPrice={orderModal.price}
        initialPositionType="LONG"
      />
    </div>
  )
}

export default Rebalance
